import { REGISTERED_MODULE_UIS } from '../modules';

/**
 * Internationalization (i18n) - Translation strings
 * Default language: French (fr)
 */

export type TranslationKey = keyof typeof translations.fr;

const baseTranslations = {
  fr: {
    // ─────────────────────────────────────────────────────────────
    // Auth
    // ─────────────────────────────────────────────────────────────
    'auth.welcome': 'Bienvenue sur Ohalys',
    // C0 — le produit n'affirme pas être certifié tant qu'il ne l'est pas.
    'auth.subtitle': "Gestion d'entreprise et comptabilité",
    // Login showcase panel. The headline is split around its accented word;
    // the three parts render as one heading.
    'login.hero.lead': 'Pilotez',
    'login.hero.accent': 'toute',
    'login.hero.tail': 'votre entreprise',
    'login.hero.body':
      'Facturation, caisse, stock, achats, paie et comptabilité SYSCOHADA réunis dans une seule application, en ligne comme hors connexion.',
    'login.feature.commerce.title': 'Gestion commerciale',
    'login.feature.commerce.description':
      'Facturation électronique (module E-Facture), caisse, stock, achats et CRM',
    'login.feature.accounting.title': 'Comptabilité et paie',
    'login.feature.accounting.description':
      'Plan SYSCOHADA, états financiers, liasse fiscale, IUTS et CNSS',
    'login.feature.security.title': 'Sécurisé',
    'login.feature.security.description': 'Données protégées et chiffrées',
    'login.modules.label': 'Modules de la suite',
    'login.modules.invoicing': 'Facturation',
    'login.modules.pos': 'Caisse',
    'login.modules.stock': 'Stock',
    'login.modules.accounting': 'Comptabilité',
    'login.modules.payroll': 'Paie',
    'login.modules.projects': 'Projets',
    'auth.login': 'Connexion',
    'auth.logout': 'Déconnexion',
    'auth.email': 'Adresse email',
    'auth.email.placeholder': 'votre@email.bf',
    'auth.password': 'Mot de passe',
    'auth.password.placeholder': 'Entrez votre mot de passe',
    'auth.rememberMe': 'Se souvenir de moi',
    'auth.forgotPassword': 'Mot de passe oublié ?',
    'auth.noAccount': 'Pas encore de compte ?',
    'auth.register': "S'inscrire",
    'auth.loginSuccess': 'Connexion réussie',
    'auth.loginError': 'Échec de la connexion',
    'auth.invalidCredentials': 'Email ou mot de passe incorrect',
    'auth.sessionExpired': 'Votre session a expiré',
    'auth.accessDenied': 'Accès refusé',
    'auth.permissionDenied': 'Permission refusée',
    'auth.refreshing': 'Actualisation de la session...',
    'auth.refreshFailed': 'Échec du rafraîchissement de la session',
    // Serveur injoignable au démarrage (429, 5xx, coupure réseau) — la
    // session n'a PAS expiré, il ne faut donc pas annoncer le contraire.
    'auth.unavailable.title': 'Service momentanément indisponible',
    'auth.unavailable.description':
      "Le serveur n'a pas répondu : trop de requêtes en cours ou incident temporaire. Votre session n'a pas expiré — réessayez dans un instant.",

    // ─────────────────────────────────────────────────────────────
    // Validation
    // ─────────────────────────────────────────────────────────────
    'validation.required': 'Ce champ est obligatoire',
    'validation.email.invalid': 'Adresse email invalide',
    'validation.password.minLength': 'Le mot de passe doit contenir au moins 8 caractères',
    'validation.password.required': 'Le mot de passe est obligatoire',

    // ─────────────────────────────────────────────────────────────
    // Common
    // ─────────────────────────────────────────────────────────────
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.success': 'Succès',
    'common.cancel': 'Annuler',
    'common.save': 'Enregistrer',
    'common.delete': 'Supprimer',
    'common.edit': 'Modifier',
    'common.create': 'Créer',
    'common.search': 'Rechercher',
    'common.filter': 'Filtrer',
    'common.export': 'Exporter',
    'common.import': 'Importer',
    'common.refresh': 'Actualiser',
    'common.back': 'Retour',
    'common.next': 'Suivant',
    'common.previous': 'Précédent',
    'common.confirm': 'Confirmer',
    'common.yes': 'Oui',
    'common.no': 'Non',
    'common.home': 'Accueil',
    'common.logout': 'Déconnexion',
    'common.close': 'Fermer',
    'common.retry': 'Réessayer',
    'common.actions': 'Actions',
    'common.type': 'Type',
    'common.status': 'Statut',
    'common.date': 'Date',
    'common.name': 'Nom',
    'common.description': 'Description',
    'common.active': 'Actif',
    'common.inactive': 'Inactif',
    'common.all': 'Tous',
    'common.none': 'Aucun',
    'common.view': 'Voir',
    'common.download': 'Télécharger',
    'common.print': 'Imprimer',
    'common.change': 'Changer',
    'common.clearFilters': 'Effacer les filtres',
    'common.backToList': 'Retour à la liste',
    'common.perPage': 'Par page :',
    'common.noRole': 'Aucun rôle',
    'common.saving': 'Enregistrement...',
    'common.deleting': 'Suppression...',
    'common.today': "Aujourd'hui",

    // ─────────────────────────────────────────────────────────────
    // Error pages
    // ─────────────────────────────────────────────────────────────
    'error.notFound.title': 'Page non trouvée',
    'error.notFound.description': "La page que vous recherchez n'existe pas.",
    'error.forbidden.title': 'Accès refusé',
    'error.forbidden.description': "Vous n'avez pas les permissions nécessaires pour accéder à cette page.",
    'error.serverError.title': 'Erreur serveur',
    'error.serverError.description': 'Une erreur inattendue est survenue. Veuillez réessayer plus tard.',
    'error.loading': 'Erreur de chargement',
    'error.generic': 'Une erreur est survenue.',
    'guide.moved.title': "Le guide utilisateur a changé d'adresse",
    'guide.moved.description':
      "Son téléchargement démarre automatiquement. S'il ne démarre pas, utilisez le bouton ci-dessous.",
    'guide.moved.download': 'Télécharger le guide utilisateur',

    // ─────────────────────────────────────────────────────────────
    // Navigation
    // ─────────────────────────────────────────────────────────────
    'nav.dashboard': 'Tableau de bord',
    'nav.hideItem': 'Masquer « {label} » du menu',
    'module.switchedOff': 'Module désactivé par l’entreprise.',
    'nav.hideItemShort': 'Masquer du menu',
    'nav.hideFailed': 'L’entrée n’a pas pu être masquée.',
    'nav.showHidden': 'Réafficher les entrées masquées ({count})',
    'nav.invoices': 'Factures',
    'nav.pos': 'Caisse (POS)',
    'nav.tables': 'Plan de salle',
    'nav.menu': 'Carte',
    'nav.stations': 'Postes',
    'nav.indicators': 'Indicateurs',
    'nav.rooms': 'Chambres',
    'nav.stays': 'Séjours',
    'nav.clients': 'Clients',
    'nav.articles': 'Articles',
    'nav.stock': 'Stock',
    'nav.reports': 'Rapports',
    'nav.settings': 'Paramètres',
    'nav.workflow': 'Workflow de validation',
    'nav.erpConnection': 'Connexion ERP',
    'nav.pispiEvents': 'Événements PI-SPI',
    'nav.nodeHealth': 'État des nœuds',
    'nav.billing': 'Abonnement',
    'nav.imports': 'Imports',
    'nav.onboarding': 'Assistant de démarrage',
    'nav.verification': 'File de vérification',
    'nav.mcfSignaturesEnDoute': 'Signatures MCF en doute',
    'nav.users': 'Utilisateurs',
    'nav.roles': 'Rôles',
    'nav.establishments': 'Établissements',
    'nav.audit': "Journal d'audit",
    'nav.facturation': 'Facturation',
    'nav.gestion': 'Gestion',
    'nav.administration': 'Administration',
    'nav.ventesCaisse': 'Ventes & Caisse',

    // Hospitalité — plan de salle
    'hospitalite.tables.title': 'Plan de salle',
    'hospitalite.tables.subtitle': 'Ouvrez une table pour démarrer un ticket',
    'hospitalite.tables.free': 'Libre',
    'hospitalite.tables.occupied': 'Occupée',
    'hospitalite.tables.seats': 'couverts',
    'hospitalite.tables.empty': 'Aucune table configurée',
    'hospitalite.tables.loading': 'Chargement du plan de salle…',
    'hospitalite.tables.sessionRequired': 'Ouvrez une session de caisse pour servir.',

    // Hospitalité — carte
    'hospitalite.menu.title': 'Carte',
    'hospitalite.menu.subtitle': 'Ce que la salle peut commander ce soir',
    'hospitalite.menu.empty': 'Aucun article de carte',
    'hospitalite.menu.available': 'Disponible',
    'hospitalite.menu.unavailable': 'Épuisé',
    'hospitalite.menu.station.CUISINE': 'Cuisine',
    'hospitalite.menu.station.BAR': 'Bar',

    // Hospitalité — postes
    'hospitalite.stations.title': 'Postes',
    'hospitalite.stations.subtitle': 'Ce qu\'il reste à préparer',
    'hospitalite.stations.empty': 'Rien en attente',
    'hospitalite.stations.noTable': 'Comptoir',

    // Hospitalité — chambres
    'hospitalite.rooms.title': 'Chambres',
    'hospitalite.rooms.subtitle': "L'état des chambres et la note en cours",
    'hospitalite.rooms.loading': 'Chargement des chambres…',
    'hospitalite.rooms.empty': 'Aucune chambre configurée',
    'hospitalite.rooms.status.LIBRE': 'Libre',
    'hospitalite.rooms.status.OCCUPEE': 'Occupée',
    'hospitalite.rooms.status.HORS_SERVICE': 'Hors service',
    'hospitalite.rooms.capacity': 'personnes',
    'hospitalite.rooms.floor': 'étage',

    // Hospitalité — indicateurs
    'hospitalite.indicators.title': 'Indicateurs',
    'hospitalite.indicators.subtitle': "Occupation, prix moyen et usage des services",
    'hospitalite.indicators.occupancy': "Taux d'occupation",
    'hospitalite.indicators.adr': 'Prix moyen par nuitée',
    'hospitalite.indicators.revpar': 'RevPAR',
    'hospitalite.indicators.revparHint': 'Recette par chambre disponible',
    'hospitalite.indicators.nightsSold': 'Nuitées vendues',
    'hospitalite.indicators.nightsAvailable': 'Nuitées disponibles',
    'hospitalite.indicators.roomRevenue': 'CA hébergement',
    'hospitalite.indicators.services': 'Usage des services',
    'hospitalite.indicators.timesSold': 'Ventes',
    'hospitalite.indicators.quantity': 'Quantité',
    'hospitalite.indicators.revenue': 'Chiffre d\'affaires',
    'hospitalite.indicators.noServices': 'Aucun service vendu sur la période',
    'hospitalite.indicators.loading': 'Calcul des indicateurs…',
    'hospitalite.indicators.from': 'Du',
    'hospitalite.indicators.to': 'au',
    'hospitalite.indicators.revenueWithoutNights':
      "Des nuitées ont été facturées sans qu'aucune nuit ne soit enregistrée sur la période : le taux d'occupation et le prix moyen ne peuvent pas être calculés, et ne se recoupent pas avec le RevPAR. Les chambres ont probablement été facturées sans passer par l'arrivée.",
    'hospitalite.indicators.caveat':
      "Les nuitées disponibles sont calculées sur les chambres louables aujourd'hui : la période antérieure à un ajout ou une mise hors service n'est pas reconstituée.",
    'hospitalite.rooms.noGuest': 'Client non renseigné',

    // Hospitalité — séjours
    'hospitalite.stays.title': 'Séjours',
    'hospitalite.stays.subtitle': 'Les clients présents et leur note',
    'hospitalite.stays.loading': 'Chargement des séjours…',
    'hospitalite.stays.empty': 'Aucun séjour en cours',
    'hospitalite.stays.room': 'Chambre',
    'hospitalite.stays.checkIn': 'Arrivée',
    'hospitalite.stays.expectedCheckOut': 'Départ prévu',
    'hospitalite.stays.guests': 'personnes',
    'hospitalite.stays.folio': 'Note',
    'hospitalite.stays.checkout': 'Clôturer le séjour',
    'hospitalite.stays.checkoutTitle': 'Clôture du séjour',
    'hospitalite.stays.checkoutFolioLoading': 'Chargement de la note…',
    'hospitalite.stays.checkoutFolioError': 'La note de ce séjour est illisible.',
    'hospitalite.stays.checkoutClose': 'Fermer',
    'hospitalite.stays.checkoutFailure': 'Échec de la clôture du séjour.',
    'hospitalite.stays.closedNotice': 'Séjour clôturé',
    'hospitalite.stays.invoiceIssued': 'Facture',
    'hospitalite.stays.invoiceView': 'Voir la facture',
    'nav.clientsCrm': 'Clients & CRM',
    'nav.catalogue': 'Catalogue & Stock',
    'nav.depots': 'Dépôts',
    'nav.inventaires': 'Inventaires',
    'nav.achats': 'Achats & Fournisseurs',
    'nav.fiscalite': 'Fiscalité',
    'nav.rapportsAnalyses': 'Rapports & analyses',
    'nav.rapportsFiscaux': 'Rapports X, Z et A',
    'nav.accounting.h.saisie': 'Saisie',
    'nav.accounting.h.etats': 'États',
    'nav.accounting.h.immos': 'Immobilisations',
    'nav.accounting.h.pilotage': 'Pilotage',
    'nav.accounting.h.referentiel': 'Référentiel',
    'nav.admin.h.acces': 'Accès',
    'nav.admin.h.structure': 'Structure',
    'nav.admin.h.integrations': 'Intégrations',
    'nav.admin.h.saas': 'Abonnement & nœuds',
    'nav.profile': 'Mon profil',
    'nav.accounting': 'Comptabilité',
    'nav.accounting.dashboard': 'Tableau de bord',
    'nav.accounting.chartOfAccounts': 'Plan comptable',
    'nav.accounting.entries': 'Écritures',
    'nav.accounting.suppliers': 'Fournisseurs',
    'nav.accounting.supplierInvoices': 'Factures fournisseurs',
    'nav.accounting.assets': 'Immobilisations',
    'nav.accounting.depreciations': 'Dotations',
    'nav.accounting.journalReport': 'Journal',
    'nav.accounting.generalLedger': 'Grand livre',
    'nav.accounting.trialBalance': 'Balance',
    'nav.accounting.analyticBalance': 'Balance analytique',
    'nav.accounting.auxiliaries': 'Tiers',
    'nav.accounting.auxiliaryBalance': 'Balance auxiliaire',
    'nav.accounting.agedBalance': 'Balance âgée',
    'nav.accounting.lettering': 'Lettrage',
    'nav.accounting.reconciliation': 'Rapprochement',
    'nav.accounting.supportingDocuments': 'Pièces justificatives',
    'nav.accounting.vat': 'TVA',
    'nav.accounting.statements': 'États financiers',
    'nav.accounting.statementsSycebnl': 'États financiers SYCEBNL',
    'nav.funding': 'Financements',
    // Moteur budgétaire (verticale ONG palier B1, Task 8).
    'nav.reporting.donor': 'Rapports bailleur',
    'nav.budget': 'Budget',
    'nav.budget.execution': 'Exécution',
    'nav.budget.envelopes': 'Enveloppes',
    'nav.budget.commitments': 'Engagements',
    'nav.budget.visas': 'Visas',
    'nav.budget.ordonnancement': 'Ordonnancement',
    'nav.accounting.donorRegister': 'Registre des donateurs',
    'nav.accounting.funds': 'Fonds affectés',
    'nav.accounting.liasseEsintax': 'Liasse fiscale (eSINTAX)',
    'nav.accounting.reportDefinitions': 'Rapports personnalisés',
    // ★ « Budgets analytiques (hérité) » et non « Budgets » : deux modules
    // budget coexistent et un comptable en UAT les a signalés comme
    // indiscernables — celui-ci (BudgetLine, par préfixe de compte et centre
    // de coût, avec sa ventilation mensuelle) et le module « Budget » de
    // premier niveau (BudgetEnvelope, AE/CP, engagements, contrôle bloquant
    // E1-E5, source des rapports bailleur). Tant que la mensualisation n'est
    // pas portée sur le moteur, les DEUX restent nécessaires : le nom doit
    // donc dire lequel est lequel.
    'nav.accounting.budgets': 'Budgets analytiques (hérité)',
    'nav.accounting.budgetVsActual': 'Réalisé vs budget',
    'nav.accounting.costCenters': 'Centres de coûts',
    'nav.accounting.analyticAxes': 'Axes analytiques',
    'nav.accounting.periods': 'Périodes',
    'nav.accounting.closing': 'Clôture',
    'nav.accounting.exchangeRates': 'Taux de change',
    'nav.accounting.settings': 'Paramètres',
    'nav.purchasing': 'Achats',
    'nav.purchasing.purchaseOrders': 'Bons de commande',
    'nav.hr': 'RH & Paie',
    'nav.hr.employees': 'Employés',
    'hr.employees.empty.noPayrollRun':
      "Les employés sont créés par un utilisateur disposant de la permission « Préparer la paie ».",
    'nav.hr.leaves': 'Congés',
    'nav.hr.payroll': 'Paie',
    'nav.hr.payrollParameters': 'Paramètres de paie',
    'nav.crm': 'CRM',
    'nav.crm.leads': 'Prospects',
    'nav.crm.opportunities': 'Opportunités',
    'nav.crm.activities': 'Activités',
    'nav.projects': 'Projets',
    'nav.projects.list': 'Projets',
    'nav.sales': 'Ventes',
    'nav.sales.quotes': 'Devis',
    'nav.sales.deliveryNotes': 'Bons de livraison',
    'nav.sales.recurring': 'Factures récurrentes',
    'nav.sales.reminders': 'Relances',
    'nav.declarations': 'Déclarations',
    'nav.declarations.list': 'Déclarations',
    'nav.declarations.calendar': 'Calendrier fiscal',
    'nav.analytics': 'Analyses',
    'nav.accounting.smt': 'Comptabilité simplifiée',
    'nav.accounting.settingsSmt': 'Paramètres comptables',
    'nav.purchasing.importCosts': 'Importations',
    'nav.cabinet': 'Portail cabinet',
    'nav.cabinetDossiers': 'Dossiers clients',
    'nav.cabinetSettings': 'Cabinet comptable',
    'nav.webhooks': 'Webhooks',

    // ─────────────────────────────────────────────────────────────
    // Branding
    // ─────────────────────────────────────────────────────────────
    'brand.name': 'Ohalys',
    'brand.tagline':
      "Gestion commerciale, comptabilité SYSCOHADA et paie pour les entreprises du Burkina Faso",
    'brand.baseline': "Suite de gestion d'entreprise",
    // Stored as one sentence (not split around brand.name in JSX) so the
    // elision stays grammatically attached to the brand. {brand} is a
    // literal placeholder substituted at the call site — a rename to a
    // name starting with a consonant would need « de » instead of « d' ».
    'pwa.update.body': "Une nouvelle version d'{brand} est prête.",

    // ─────────────────────────────────────────────────────────────
    // Offline queue — modifications held from the current session
    // ─────────────────────────────────────────────────────────────
    'offline.held.banner.owner': 'appartenant à un autre dossier ou à un autre utilisateur',
    'offline.held.intro':
      "Ces modifications n'appartiennent pas au dossier ouvert ou à l'utilisateur connecté. Elles ne seront jamais envoyées sous la session en cours.",
    'offline.held.reason.unknownTenant':
      "Dossier d'origine inconnu — enregistrée avant la mise à jour. Elle ne sera jamais envoyée automatiquement : vérifiez si la saisie doit être refaite, puis ignorez-la.",
    'offline.held.reason.unknownUser':
      "Utilisateur d'origine inconnu — enregistrée avant la mise à jour. Elle ne sera jamais envoyée automatiquement : vérifiez si la saisie doit être refaite, puis ignorez-la.",
    'offline.held.reason.otherUser':
      'Enregistrée par un autre utilisateur — elle sera envoyée automatiquement lors de sa prochaine connexion à ce dossier.',
    'offline.held.reason.otherTenant':
      "Enregistrée sous un autre dossier — elle sera envoyée automatiquement lors d'une prochaine connexion à ce dossier.",

    // ─────────────────────────────────────────────────────────────
    // Physical inventory — scan and offline counting
    // ─────────────────────────────────────────────────────────────
    'inventory.scan.unknown': "Aucune ligne de cet inventaire ne correspond au code « {code} ». Rien n'a été compté.",
    'inventory.scan.lookupFailed': 'Recherche du code « {code} » impossible sans connexion : saisissez le code article.',
    'inventory.scan.found': '{article} : saisissez la quantité comptée.',
    'inventory.offline.queued':
      'Hors ligne : le comptage est gardé sur cet appareil et sera envoyé dès le retour de la connexion.',
    'inventory.offline.title': 'Saisies hors ligne',
    'inventory.offline.pending': 'En attente de synchronisation',
    'inventory.offline.refused': 'Refusée',
    'inventory.offline.retry': 'Réessayer',
    'inventory.offline.discard': 'Abandonner',
    'inventory.movements.limits':
      "Une validation écrit au plus {standard} mouvements de stock, {yearEnd} pour une fin d'exercice : un par lot compté avec un écart, plus un pour le reste de la ligne. Au-delà, elle est refusée.",
    'inventory.movements.planned': 'Mouvements prévus à la validation : {planned} (limite : {limit}).',
    'inventory.movements.over':
      'La validation sera refusée : choisissez « Faire recompter » sur les lignes à plusieurs lots, puis « Revenir au comptage », et saisissez le total de ces articles sans nom de lot. Ces lots restent alors tels quels dans le stock.',
    'inventory.movements.offHours':
      'Plus de {standard} mouvements : validez hors des heures de caisse, les ventes de ces articles attendent la fin de la validation.',
    'inventory.validation.explain':
      'La validation enregistre un mouvement « Inventaire » par lot en écart et un pour le reste de chaque ligne, sur le stock du moment. Elle ne se défait pas : une erreur se corrige par un nouvel inventaire ou un ajustement.',
    'inventory.thresholds.help':
      "Au passage en revue, une ligne dont les mouvements dépassent l'un de ces seuils est à recompter, et le validateur ne peut pas valider seul une telle ligne qu'il a été seul à compter. Sans seuil, aucun recomptage n'est imposé.",
    'inventory.thresholds.pctLabel': 'Mouvements en % du stock théorique',
    'inventory.thresholds.valueLabel': 'Mouvements en valeur au CUMP',
    'inventory.thresholds.summary': ' · Recomptage au-delà de {limits}.',
    'inventory.thresholds.pct': '{pct} % de mouvements',
    'inventory.thresholds.value': '{value} de valeur de mouvements',
    'inventory.thresholds.or': ' ou ',
    'stock.adjust.lot.label': 'Lot',
    'stock.adjust.lot.automatic': "Automatique : la date la plus proche d'abord, les lots périmés en dernier",
    'stock.adjust.lot.noDate': 'sans date',
    'stock.adjust.lot.help': 'Choisissez le lot quand vous savez lequel sort : seul ce lot diminue.',
    'stock.adjust.lot.pastHint':
      "Pour retirer des produits périmés, choisissez leur lot : en automatique, ils sortent en dernier et le lot encore valable diminue d'abord.",
    'stock.adjust.lot.drawn': 'Lots déduits : {lots}.',
    'stock.adjust.lot.recovery':
      "En cas d'erreur de saisie, une entrée ne remet rien dans le lot : corrigez-le par un inventaire qui nomme le lot.",
    'stock.adjust.lot.unread':
      "Les lots de ce dépôt n'ont pas pu être lus : en automatique, la sortie suit la date la plus proche.",
    'inventory.batch.label': 'Lot compté',
    'inventory.batch.none': 'Sans lot',
    'inventory.batch.noneHint': "Sans lot choisi, les lots de cet article restent tels quels, sauf si vous comptez zéro et que son stock tombe à zéro.",
    'inventory.lotsAbove.summary':
      '{count} ligne(s) laisseront des lots au-dessus du stock à la validation : {codes}. Le détail figure sous chacune.',
    'inventory.lotsAbove.line': 'À la validation, les lots de cette ligne porteront {lots} pour un stock de {book}.',
    'inventory.lotsAbove.left': 'Restent tels quels : {lots}.',
    'inventory.lotsAbove.action':
      'Pour les corriger, choisissez « Faire recompter » puis « Revenir au comptage », et saisissez chaque lot en le choisissant, à zéro s\'il n\'y est plus.',
    'inventory.batch.other': 'Autre lot…',
    'inventory.batch.number': 'Numéro de lot',
    'inventory.batch.expiry': 'Date de péremption',
    'inventory.batch.numberRequired': 'Saisissez le numéro du lot.',
    'inventory.batch.expired': 'périmé',
    'inventory.batch.expiresOn': 'DLC {date}',
    'inventory.batch.chosen': 'lot choisi dans la liste',
    'inventory.batch.title': 'Lots',
    'inventory.batch.unavailable': 'Liste des lots indisponible : pour nommer un lot, choisissez « Autre lot… » et saisissez son numéro.',
    'inventory.batch.noLine': "Cet article n'a pas encore de ligne dans cet inventaire : cette saisie sera enregistrée sans lot.",
    'inventory.batch.recordedWithout': 'Saisie enregistrée sans lot.',
    'inventory.late.badge': 'Saisie tardive',
    'inventory.late.badgeMany': '{count} saisies tardives',

    // ─────────────────────────────────────────────────────────────
    // Pagination
    // ─────────────────────────────────────────────────────────────
    'pagination.showing': 'Affichage de',
    'pagination.to': 'à',
    'pagination.of': 'sur',
    'pagination.page': 'Page',
    'pagination.results': 'résultats',

    // ─────────────────────────────────────────────────────────────
    // Dashboard
    // ─────────────────────────────────────────────────────────────
    'dashboard.title': 'Tableau de bord',
    'dashboard.welcome': 'Bienvenue sur Ohalys - Votre tableau de bord',
    'dashboard.error.loadFailed': 'Impossible de charger certaines données du tableau de bord. Vérifiez votre connexion et rechargez la page.',
    'dashboard.kpi.activeClients': 'Clients actifs',
    'dashboard.kpi.activeArticles': 'Articles actifs',
    'dashboard.kpi.lowStock': 'Articles en stock bas',
    'dashboard.kpi.monthInvoices': 'Factures du mois',
    'dashboard.kpi.totalClients': 'clients au total',
    'dashboard.kpi.totalArticles': 'articles au total',
    'dashboard.kpi.needRestock': 'Besoin de réapprovisionnement',
    'dashboard.kpi.overdueInvoices': 'facture(s) en retard',
    'dashboard.kpi.signedThisMonth': 'Factures signées ce mois',
    'dashboard.quickActions.title': 'Actions rapides',
    'dashboard.quickActions.subtitle': 'Accédez rapidement aux fonctionnalités principales',
    'dashboard.quickActions.newInvoice': 'Nouvelle facture',
    'dashboard.quickActions.newClient': 'Nouveau client',
    'dashboard.quickActions.newArticle': 'Nouvel article',
    'dashboard.recentInvoices.title': 'Dernières factures',
    'dashboard.recentInvoices.subtitle': 'Les 5 dernières factures signées',
    'dashboard.recentInvoices.empty': 'Aucune facture signée pour le moment',
    'dashboard.recentInvoices.cashClient': 'Client comptant',
    'dashboard.stats.clientBreakdown': 'Répartition des clients',
    'dashboard.stats.articleBreakdown': 'Répartition des articles',
    'dashboard.stats.clientCC': 'Client Comptant (CC)',
    'dashboard.stats.clientPM': 'Personne Morale (PM)',
    'dashboard.stats.clientPP': 'Personne Physique (PP)',
    'dashboard.stats.clientPC': 'Personne Physique Commerçant (PC)',
    'dashboard.stats.goods': 'Biens',
    'dashboard.stats.services': 'Services',
    'dashboard.stats.favorites': 'Favoris',

    // Blocs servis par /dashboard/summary
    'dashboard.revenue.title': "Chiffre d'affaires",
    'dashboard.revenue.description': 'facture(s) sur la période',
    'dashboard.receivables.title': 'Créances clients',
    'dashboard.receivables.description': 'créance(s) à encaisser',
    'dashboard.receivables.overdue': 'dont en retard',
    'dashboard.topArticles.title': 'Meilleures ventes',
    'dashboard.topArticles.subtitle': 'Les 5 articles les plus vendus sur la période',
    'dashboard.mySales.title': 'Mes ventes',
    'dashboard.mySales.description': 'facture(s) signée(s) aujourd’hui',
    'dashboard.till.title': 'Ma caisse',
    'dashboard.till.subtitle': 'Session ouverte',
    'dashboard.till.opening': 'Fonds de caisse',
    'dashboard.till.sales': 'Ventes',
    'dashboard.till.deposits': 'Apports',
    'dashboard.till.withdrawals': 'Retraits',
    'dashboard.till.refunds': 'Remboursements',
    'dashboard.till.expected': 'Solde théorique',
    'dashboard.pendingTickets.title': 'Tickets en attente',
    'dashboard.pendingTickets.description': 'ticket(s) suspendu(s)',
    'dashboard.stock.title': 'Valeur du stock',
    'dashboard.stock.description': 'article(s) suivi(s) en stock',
    'dashboard.stock.unvalued': 'sans CUMP non valorisé(s)',
    'dashboard.stock.unavailable': 'Non disponible',
    'dashboard.nearExpiry.title': 'Lots proches de la péremption',
    'dashboard.nearExpiry.subtitle': 'Lots à consommer dans les 30 jours',
    'dashboard.nearExpiry.days': 'j',
    'dashboard.movements.title': 'Mouvements du jour',
    'dashboard.movements.description': 'mouvement(s) de stock aujourd’hui',
    'dashboard.verification.title': 'Factures à vérifier',
    'dashboard.verification.description': 'en attente de votre contrôle',
    'dashboard.mcfDoubt.title': 'Signatures en doute',
    'dashboard.mcfDoubt.description': 'à arbitrer avec le rapport X/Z',
    'dashboard.treasury.title': 'Trésorerie',
    'dashboard.treasury.description': 'Banque et caisse au grand livre',
    'dashboard.margin.title': 'Marge sur stock',
    'dashboard.margin.description': 'Indicatif — hors retours',
    'dashboard.accounting.title': 'Santé comptable',
    'dashboard.accounting.subtitle': 'État de l’exercice',
    'dashboard.accounting.draftEntries': 'Écritures en brouillon',
    'dashboard.accounting.outboxPending': 'Comptabilisations en attente',
    'dashboard.accounting.outboxFailed': 'Comptabilisations en échec',
    'dashboard.accounting.periodsLocked': 'Périodes verrouillées',
    'dashboard.accounting.balanced': 'Balance équilibrée',
    'dashboard.accounting.openPeriod': 'Période ouverte',
    'dashboard.hr.headcount.title': 'Effectif',
    'dashboard.hr.headcount.description': 'salarié(s) en activité',
    'dashboard.hr.leave.title': 'Congés à traiter',
    'dashboard.hr.leave.description': 'jour(s) accordé(s) cette année',
    'dashboard.hr.contracts.title': 'Contrats à échéance',
    'dashboard.hr.contracts.description': 'dans les 60 jours',
    'dashboard.payroll.title': 'Paie',
    'dashboard.payroll.subtitle': 'Cycles de l’année',
    'dashboard.payroll.gross': 'Masse salariale brute',
    'dashboard.payroll.net': 'Net à payer',
    'dashboard.payroll.employerCharges': 'Charges patronales',
    'dashboard.budget.title': 'Exécution budgétaire',
    'dashboard.budget.description': 'engagé sur l’exercice',
    'dashboard.budget.queues.title': 'Chaîne de la dépense',
    'dashboard.budget.queues.subtitle': 'Ce qui attend une étape',
    'dashboard.budget.queues.awaitingVisa': 'Engagements à viser',
    'dashboard.budget.queues.toOrder': 'Liquidé non ordonnancé',
    'dashboard.budget.queues.toPay': 'Ordonnancé non payé',
    'dashboard.establishments.title': 'Établissements',
    'dashboard.establishments.description': 'site(s) actif(s)',
    'dashboard.common.notDeterminable': 'Non déterminable',
    'dashboard.error.blocksFailed': 'Certains indicateurs n’ont pas pu être calculés et ne sont pas affichés. Rechargez la page ; s’ils manquent toujours, signalez-le.',
    'dashboard.common.yes': 'Oui',
    'dashboard.common.no': 'Non',

    // ─────────────────────────────────────────────────────────────
    // Invoices
    // ─────────────────────────────────────────────────────────────
    'invoices.title': 'Factures',
    'invoices.subtitle': 'Gérez vos factures électroniques',
    'invoices.new': 'Nouvelle facture',
    'invoices.rrr': 'Avoir RRR',
    'invoices.fromFile': 'Depuis un fichier',
    'invoices.create': 'Créer une facture',
    'invoices.search.placeholder': 'Numéro ou client...',
    'invoices.filter.type': 'Type',
    'invoices.filter.status': 'Statut',
    'invoices.filter.payment': 'Paiement',
    'invoices.filter.establishment': 'Établissement',
    'invoices.filter.allTypes': 'Tous les types',
    'invoices.filter.allStatuses': 'Tous les statuts',
    'invoices.filter.allPayments': 'Tous les paiements',
    'invoices.filter.allEstablishments': 'Tous les établissements',
    'invoices.table.number': 'Numéro',
    'invoices.table.date': 'Date',
    'invoices.table.client': 'Client',
    'invoices.table.type': 'Type',
    'invoices.table.totalTTC': 'Total TTC',
    'invoices.table.status': 'Statut',
    'invoices.table.payment': 'Paiement',
    'invoices.table.actions': 'Actions',
    'invoices.type.FV': 'Facture de Vente',
    'invoices.type.FA': "Facture d'Avoir",
    'invoices.type.FT': "Facture d'Acompte",
    'invoices.type.EV': 'Export Vente',
    'invoices.type.ET': 'Export Acompte',
    'invoices.type.EA': 'Export Avoir',
    'invoices.status.draft': 'Brouillon',
    'invoices.status.signed': 'Signée',
    // Status of an issued invoice: the device named as the PDF's mention names it.
    'invoices.issue.certified': 'Certifiée',
    'invoices.issue.nonCertified.simulator': 'Non certifiée — simulateur',
    'invoices.issue.nonCertified.virtual': 'Non certifiée — MCF virtuel',
    'invoices.issue.nonCertified.unknown': 'Non certifiée — dispositif inconnu',
    'invoices.issue.ordinary': 'Facture ordinaire',
    'invoices.payment.pending': 'Non payée',
    'invoices.payment.partial': 'Partiellement payée',
    'invoices.payment.paid': 'Payée',
    'invoices.payment.partialShort': 'Partielle',
    'invoices.empty.title': 'Aucune facture',
    'invoices.empty.filtered': 'Aucune facture trouvée',
    'invoices.empty.description': 'Commencez par créer votre première facture pour gérer votre facturation électronique.',
    'invoices.empty.filteredDescription': 'Aucune facture ne correspond à vos critères de recherche. Essayez de modifier vos filtres.',
    'invoices.error.loadFailed': 'Une erreur est survenue lors du chargement des factures.',
    'invoices.delete.confirm': 'Confirmer la suppression',
    'invoices.delete.message': 'Êtes-vous sûr de vouloir supprimer la facture',
    'invoices.delete.irreversible': 'Cette action est irréversible.',
    'invoices.delete.success': 'Facture annulée avec succès',
    'invoices.delete.error': 'Erreur lors de la suppression de la facture',
    'invoices.tooltip.viewDetails': 'Voir les détails',
    'invoices.tooltip.delete': 'Supprimer',
    'invoices.cashClient': 'Client comptant',

    // ─────────────────────────────────────────────────────────────
    // Invoice Create
    // ─────────────────────────────────────────────────────────────
    'invoices.create.title': 'Nouvelle facture',
    'invoices.create.subtitle': 'Créez une nouvelle facture électronique',
    'invoices.create.invoiceType': 'Type de facture',
    'invoices.create.invoiceTypeDesc': 'Sélectionnez le type de facture et le mode de tarification',
    'invoices.create.priceMode': 'Mode de prix',
    'invoices.create.priceModeHT': 'Hors Taxe (HT)',
    'invoices.create.priceModeTTC': 'Toutes Taxes Comprises (TTC)',
    'invoices.create.clientInfo': 'Informations client',
    'invoices.create.clientInfoDesc': 'Sélectionnez un client existant ou saisissez les informations manuellement',
    'invoices.create.searchClient': 'Rechercher un client',
    'invoices.create.searchClient.placeholder': 'Rechercher un client...',
    'invoices.create.clientType': 'Type de client',
    'invoices.create.clientName': 'Nom du client *',
    'invoices.create.clientNamePlaceholder': 'Nom ou raison sociale',
    'invoices.create.clientIfu': 'IFU *',
    'invoices.create.clientIfuPlaceholder': '8 chiffres + 1 lettre',
    'invoices.create.clientNoName': 'Client sans nom',
    'invoices.create.lines': 'Lignes de facture',
    'invoices.create.linesDesc': 'Ajoutez les articles ou services à facturer',
    'invoices.create.addArticle': 'Ajouter un article',
    'invoices.create.searchArticle.placeholder': 'Rechercher un article...',
    'invoices.create.noLines': 'Aucune ligne ajoutée',
    'invoices.create.noLinesHint': "Recherchez un article ci-dessus pour l'ajouter",
    'invoices.create.lineArticle': 'Article',
    'invoices.create.lineTax': 'Taxe',
    'invoices.create.lineQty': 'Qté',
    'invoices.create.lineUnitPrice': 'Prix unit.',
    'invoices.create.lineDiscount': 'Remise',
    'invoices.create.lineTotalHT': 'Total HT',
    'invoices.create.lineTVA': 'TVA',
    'invoices.create.lineTotalTTC': 'Total TTC',
    'invoices.create.notes': 'Notes',
    'invoices.create.notesDesc': 'Ajoutez des notes ou commentaires à la facture (optionnel)',
    'invoices.create.notesPlaceholder': 'Notes internes ou commentaires...',
    'invoices.create.counterValue': 'Contre-valeur indicative',
    'invoices.create.counterValueDesc': "Montant en devise étrangère imprimé à titre indicatif sur la facture signée. La facture reste certifiée en XOF ; la contre-valeur n'est pas transmise au MCF.",
    'invoices.create.counterValueCurrency': 'Devise',
    'invoices.create.counterValueNone': 'Aucune',
    'invoices.create.counterValueRate': 'Taux (XOF pour 1 unité)',
    'invoices.create.counterValueFixedParity': 'Parité fixe : 1 EUR = 655,957 XOF',
    'invoices.create.counterValueStoredRate': 'Dernier taux enregistré, du',
    'invoices.create.counterValueNoRate': 'Aucun taux enregistré pour cette devise : saisissez le taux.',
    'invoices.create.counterValueEstimate': 'Contre-valeur estimée',
    'invoices.create.counterValueFrozen': 'taux et montant figés à la signature',
    'invoices.create.summary': 'Récapitulatif',
    'invoices.create.totalHT': 'Total HT',
    'invoices.create.totalTVA': 'TVA',
    'invoices.create.totalTTC': 'Total TTC',
    'invoices.create.saveDraft': 'Enregistrer le brouillon',
    'invoices.create.saving': 'Création...',
    'invoices.create.validationHints': 'Vérifiez les points suivants:',
    'invoices.exportVat.title': "Facture d'exportation :",
    'invoices.exportVat.line': "l'article {article} est au groupe {group} (TVA {rate}).",
    'invoices.exportVat.advice.one': "Sont exonérées de TVA les exportations directes de biens matériels et immatériels, les réexportations en suite de régime douanier suspensif et les exportations de services (CGI art. 307 2)). Vérifiez le groupe de cet article : pour une exportation, utilisez un article au groupe D.",
    'invoices.exportVat.advice.many': "Sont exonérées de TVA les exportations directes de biens matériels et immatériels, les réexportations en suite de régime douanier suspensif et les exportations de services (CGI art. 307 2)). Vérifiez le groupe de ces articles : pour une exportation, utilisez un article au groupe D.",
    'invoices.exportVat.advice.specialTax.one': "Sont exonérées de TVA les exportations directes de biens matériels et immatériels, les réexportations en suite de régime douanier suspensif et les exportations de services (CGI art. 307 2)). Vérifiez le type de facture et le groupe de cet article.",
    'invoices.exportVat.advice.specialTax.many': "Sont exonérées de TVA les exportations directes de biens matériels et immatériels, les réexportations en suite de régime douanier suspensif et les exportations de services (CGI art. 307 2)). Vérifiez le type de facture et le groupe de ces articles.",
    'invoices.exportVat.creditNote.title': "Facture d'avoir à l'exportation :",
    'invoices.exportVat.creditNote.lineOfOriginal': "la ligne {article} reprend le groupe {group} (TVA {rate}) de la facture d'origine.",
    'invoices.exportVat.creditNote.adviceOriginal': "Sont exonérées de TVA les exportations directes de biens matériels et immatériels, les réexportations en suite de régime douanier suspensif et les exportations de services (CGI art. 307 2)). Vérifiez la facture d'origine.",
    'invoices.exportVat.creditNote.adviceArticle': "Sont exonérées de TVA les exportations directes de biens matériels et immatériels, les réexportations en suite de régime douanier suspensif et les exportations de services (CGI art. 307 2)). Vérifiez le groupe de chaque article cité.",
    'invoices.create.ifuRequired': 'IFU obligatoire pour ce type de client',
    'invoices.create.ifuInvalid': "IFU invalide : 8 chiffres suivis d'1 lettre majuscule (ex : 12345678A)",
    'invoices.create.nameRequired': 'Nom du client obligatoire',
    'invoices.create.qtyPricePositive': 'Quantité et prix doivent être positifs',
    'invoices.create.addArticleHint': 'Ajoutez au moins un article pour créer la facture.',
    'invoices.create.success': 'Facture créée avec succès',
    'invoices.create.error': 'Erreur lors de la création de la facture',
    'invoices.create.offlineQueued': 'Facture enregistrée hors ligne. Elle sera synchronisée automatiquement.',
    'invoices.create.fillRequired': 'Veuillez remplir tous les champs obligatoires',
    'invoices.create.noDgiType': "Cet article n'a pas de type DGI configuré. Veuillez le mettre à jour.",
    'invoices.externalSales.title': 'Ventes antérieures à la bascule',
    'invoices.externalSales.subtitle':
      "Ventes émises par votre ancien système de facturation, enregistrées telles qu'imprimées pour pouvoir émettre un avoir",
    'invoices.externalSales.register': 'Enregistrer une vente antérieure',
    'invoices.externalSales.edit': 'Modifier la vente',
    'invoices.externalSales.delete': 'Supprimer la vente',
    'invoices.externalSales.formHint':
      'Saisissez la facture telle qu’elle est imprimée. Elle ne peut plus être modifiée dès qu’un avoir y fait référence.',
    'invoices.externalSales.reference': "Numéro de la facture d'origine",
    'invoices.externalSales.issuedOn': 'Date de la facture',
    'invoices.externalSales.invoiceType': 'Type de facture',
    'invoices.externalSales.currency': 'Devise',
    'invoices.externalSales.client': 'Client',
    'invoices.externalSales.clientType': 'Type de client',
    'invoices.externalSales.clientName': 'Nom du client',
    'invoices.externalSales.groups': 'Montants par groupe de taxation',
    'invoices.externalSales.group': 'Groupe',
    'invoices.externalSales.totalHT': 'Total HT',
    'invoices.externalSales.specificTax': 'Taxe spécifique',
    'invoices.externalSales.addGroup': 'Ajouter un groupe',
    'invoices.externalSales.removeGroup': 'Retirer ce groupe',
    'invoices.externalSales.totalTTC': 'Total TTC',
    'invoices.externalSales.ttcGap':
      'Le total TTC saisi diffère de {gap} de la somme HT + TVA + TSB + taxe spécifique. Il est enregistré tel quel.',
    'invoices.externalSales.certification': 'Éléments de certification MCF (facultatif)',
    'invoices.externalSales.certificationHint':
      'À reprendre de la facture si elle a été certifiée par un MCF : SECeF NIM, compteurs FC/TC, code SECeF/DGI, date et heure SECeF.',
    'invoices.externalSales.typeCounter': 'Compteur par type (FC)',
    'invoices.externalSales.totalCounter': 'Compteur total (TC)',
    'invoices.externalSales.secefDateTime': 'Date et heure SECeF',
    'invoices.externalSales.counters': 'compteurs',
    'invoices.externalSales.noCertification': 'Facture sans éléments de certification MCF',
    'invoices.externalSales.rrrOnly':
      'Sans NIM ni compteur total, le MCF ne peut certifier sur cette vente qu’un avoir de nature RRR.',
    'invoices.externalSales.remaining': 'Reste créditable (TTC)',
    'invoices.externalSales.remainingVat': 'dont TVA restante',
    'invoices.externalSales.remainingRounding':
      'Les avoirs ont rendu quelques centimes de plus que la vente : l’arrondi du MCF de chaque montant imprimé de la vente (TVA, prélèvements, TTC, PSVB), au plus 0,01 chacun, et de chaque ligne d’avoir, au plus l’arrondi que son calcul peut produire.',
    'invoices.externalSales.creditNotes': 'Avoirs',
    'invoices.externalSales.issueCreditNote': 'Émettre un avoir',
    'invoices.externalSales.frozen': 'Un avoir fait référence à cette vente : elle ne peut plus être modifiée ni supprimée.',
    'invoices.externalSales.save': 'Enregistrer la vente',
    'invoices.externalSales.saved': 'Vente antérieure enregistrée',
    'invoices.externalSales.deleted': 'Vente antérieure supprimée',
    'invoices.externalSales.saveError': "Erreur lors de l'enregistrement de la vente antérieure",
    'invoices.externalSales.search': 'Rechercher par numéro ou client',
    'invoices.externalSales.empty': 'Aucune vente antérieure enregistrée',
    'invoices.externalSales.creditNoteTitle': 'Avoir sur une vente antérieure',
    'invoices.externalSales.creditNoteSubtitle': 'Avoir sur une facture émise par votre ancien système',
    'invoices.externalSales.originalSale': "Vente d'origine",
    'invoices.externalSales.success': 'Avoir créé avec succès',
    'invoices.debitNote.action': 'Émettre une note de débit',
    'invoices.debitNote.title': 'Note de débit',
    'invoices.debitNote.subtitle': "Facture de vente complémentaire d'une facture émise",
    'invoices.debitNote.original': "Facture d'origine",
    'invoices.debitNote.reason': 'Motif',
    'invoices.debitNote.reasonPlaceholder': 'Choisir un motif',
    'invoices.debitNote.detail': 'Détail du motif',
    'invoices.debitNote.detailPlaceholder': "Précisez l'ajustement",
    'invoices.debitNote.reasonRequired': 'Choisissez le motif de la note de débit',
    'invoices.debitNote.detailRequired': 'Précisez le détail du motif de la note de débit',
    'invoices.debitNote.success': 'Note de débit créée',
    'invoices.debitNote.listTitle': 'Notes de débit',
    'invoices.debitNote.of': 'Note de débit de la facture',
    'invoices.create.rrr.title': 'Avoir RRR',
    'invoices.create.rrr.subtitle': "Remise, ristourne ou rabais accordé sans facture d'origine",
    'invoices.create.rrr.nature': "Nature de l'avoir",
    'invoices.create.rrr.reference': "Référence de facture d'origine : RRR",
    'invoices.create.rrr.clientInfoDesc': 'Sélectionnez un client identifié (PM, PP ou PC) dans vos fiches',
    'invoices.create.rrr.clientRequired': 'Client identifié obligatoire pour un avoir RRR',
    'invoices.create.rrr.amountsHint': 'Montants crédités au client, enregistrés en négatif sur l\'avoir.',
    'invoices.create.rrr.save': "Enregistrer l'avoir",
    'invoices.create.rrr.success': 'Avoir RRR créé avec succès',
    'invoices.create.rrr.error': "Erreur lors de la création de l'avoir",
    'invoices.create.noArticleMatch':
      'Aucun article ne correspond à cette recherche. La recherche porte sur le code, le nom, la description et la catégorie.',
    'invoices.create.FV.label': 'Facture de Vente',
    'invoices.create.FV.desc': 'Facture standard pour vente locale',
    'invoices.create.FT.label': "Facture d'Acompte",
    'invoices.create.FT.desc': 'Facture pour paiement partiel',
    'invoices.create.EV.label': 'Export Vente',
    'invoices.create.EV.desc': "Facture pour vente à l'export",
    'invoices.create.ET.label': 'Export Acompte',
    'invoices.create.ET.desc': "Facture tenant lieu à l'export",
    'invoices.create.CC.label': 'Client Comptant',
    'invoices.create.CC.desc': 'Client occasionnel sans identification',
    'invoices.create.PM.label': 'Personne Morale',
    'invoices.create.PM.desc': 'Entreprise avec IFU obligatoire',
    'invoices.create.PP.label': 'Personne Physique',
    'invoices.create.PP.desc': 'Particulier sans activité commerciale',
    'invoices.create.PC.label': 'Personne Physique Commerçante',
    'invoices.create.PC.desc': 'Commerçant avec IFU obligatoire',

    // ─────────────────────────────────────────────────────────────
    // Settings — rail de navigation secondaire (GitLab #36)
    // ─────────────────────────────────────────────────────────────
    'settings.nav.entreprise': 'Entreprise',
    'settings.nav.modules': 'Modules complémentaires',
    'settings.nav.fiscalite': 'Fiscalité (DGI)',
    'settings.nav.facturation': 'Facturation',
    'settings.nav.fidelite': 'Fidélité',
    'settings.nav.caisse': 'Caisse',
    'settings.nav.modulesMenus': 'Modules et menus',
    'settings.modulesMenus.title': 'Modules et menus',
    'settings.modulesMenus.description': 'Désactivez les modules dont votre entreprise ne se sert pas. Un module désactivé disparaît du menu et de l’assistant pour tous les utilisateurs, le serveur refuse l’accès à ses données, et le tableau de bord, la recherche et les indicateurs ne les affichent plus. Seuls les modules que votre offre, vos modules complémentaires et vos droits font apparaître sont listés.',
    'settings.modulesMenus.protected': 'Restent toujours actifs : le tableau de bord, la facturation et sa vérification, la caisse, l’encaissement des séjours, les factures récurrentes, les relances, les clients, les articles, les fournisseurs, les rapports X, Z et A, les paramètres, les utilisateurs, les rôles, les établissements, le journal d’audit et l’abonnement.',
    'settings.modulesMenus.alsoRemoves': 'Retire aussi : {entries}.',
    'settings.modulesMenus.empty': 'Aucun module ne peut être désactivé avec votre offre et vos droits.',
    'settings.modulesMenus.save': 'Enregistrer',
    'settings.modulesMenus.saved': 'Modules et menus enregistrés.',
    'settings.modulesMenus.saveFailed': 'Les modules et menus n’ont pas pu être enregistrés.',
    'settings.modulesMenus.loadFailed': 'Chargement des modules et menus impossible.',
    'settings.caisse.paymentMethods.title': 'Moyens de paiement',
    'settings.caisse.paymentMethods.description': 'Les moyens de paiement que proposent la caisse et l’enregistrement d’un règlement sur une facture. Un moyen désactivé est refusé à l’encaissement ; les paiements déjà enregistrés ne changent pas.',
    'settings.caisse.paymentMethods.cashAlways': 'Toujours accepté',
    'settings.caisse.paymentMethods.pointsHidden': 'Masqué tant que le programme de fidélité est désactivé (Paramètres › Fidélité).',
    'settings.caisse.paymentMethods.operators': 'Opérateurs Mobile Money proposés : {operators}. Ce sont ceux de vos comptes de trésorerie Mobile Money, ou tous les opérateurs si aucun compte n’en nomme un.',
    'settings.caisse.paymentMethods.atIssue': 'Les règlements saisis à l’émission d’une facture restent ouverts à tous les moyens.',
    'settings.caisse.paymentMethods.save': 'Enregistrer',
    'settings.caisse.paymentMethods.saved': 'Moyens de paiement enregistrés.',
    'settings.caisse.paymentMethods.saveFailed': 'Les moyens de paiement n’ont pas pu être enregistrés.',
    'settings.caisse.paymentMethods.loadFailed': 'Chargement des moyens de paiement impossible.',
    // Dashboard customisation (« Personnaliser » and Paramètres › Tableau de bord)
    'dashboard.widget.alerts': 'Alertes',
    'dashboard.widget.kpi-clients': 'Clients actifs',
    'dashboard.widget.kpi-articles': 'Articles actifs',
    'dashboard.widget.kpi-low-stock': 'Stock bas',
    'dashboard.widget.kpi-month-invoices': 'Factures du mois',
    'dashboard.widget.quick-actions': 'Actions rapides',
    'dashboard.widget.recent-invoices': 'Dernières factures',
    'dashboard.widget.client-breakdown': 'Répartition des clients',
    'dashboard.widget.article-breakdown': 'Répartition des articles',
    'dashboard.widget.kpi-revenue': 'Chiffre d’affaires',
    'dashboard.widget.kpi-receivables': 'Créances clients',
    'dashboard.widget.top-articles': 'Articles les plus vendus',
    'dashboard.widget.my-sales': 'Mes ventes',
    'dashboard.widget.till-today': 'Ma caisse du jour',
    'dashboard.widget.pending-tickets': 'Tickets en attente',
    'dashboard.widget.stock-overview': 'Vue d’ensemble du stock',
    'dashboard.widget.near-expiry': 'Péremptions proches',
    'dashboard.widget.stock-movements-today': 'Mouvements de stock du jour',
    'dashboard.widget.verification-queue': 'File de vérification',
    'dashboard.widget.mcf-doubt': 'Signatures MCF en doute',
    'dashboard.widget.treasury': 'Trésorerie',
    'dashboard.widget.kpi-margin': 'Marge',
    'dashboard.widget.accounting-health': 'État de la comptabilité',
    'dashboard.widget.hr-headcount': 'Effectif',
    'dashboard.widget.hr-leave': 'Congés',
    'dashboard.widget.hr-contracts': 'Contrats',
    'dashboard.widget.payroll-status': 'Paie',
    'dashboard.widget.budget-execution': 'Exécution budgétaire',
    'dashboard.widget.budget-queues': 'Files budgétaires',
    'dashboard.widget.establishments': 'Établissements',
    'dashboard.customize.button': 'Personnaliser',
    'dashboard.customize.title': 'Personnaliser le tableau de bord',
    'dashboard.customize.help': 'Cochez les blocs à afficher et changez leur ordre avec les flèches. Vous ne pouvez choisir que parmi les blocs que votre rôle, vos droits et votre offre autorisent.',
    'dashboard.customize.moveUp': 'Monter {widget}',
    'dashboard.customize.moveDown': 'Descendre {widget}',
    'dashboard.customize.atLeastOne': 'Gardez au moins un bloc affiché.',
    'dashboard.customize.save': 'Enregistrer mon tableau de bord',
    'dashboard.customize.cancel': 'Annuler',
    'dashboard.customize.reset': 'Revenir au réglage de l’entreprise',
    'dashboard.customize.saved': 'Votre tableau de bord est enregistré.',
    'dashboard.customize.resetDone': 'Le réglage de l’entreprise s’applique de nouveau.',
    'dashboard.customize.saveFailed': 'Votre tableau de bord n’a pas pu être enregistré.',
    // Widgets of the accounting, firm and stock dashboards
    'dashboard.accounting.widget.kpi-result': 'Résultat provisoire',
    'dashboard.accounting.widget.kpi-treasury': 'Trésorerie',
    'dashboard.accounting.widget.kpi-receivables': 'Créances clients',
    'dashboard.accounting.widget.kpi-vat': 'TVA du mois',
    'dashboard.accounting.widget.recent-entries': 'Dernières écritures',
    'dashboard.accounting.widget.month-activity': 'Activité du mois',
    'dashboard.accounting.widget.income-expense': 'Produits et charges',
    'dashboard.accounting.widget.budget-alerts': 'Budget',
    'dashboard.firm.widget.dossier-indicators': 'Indicateurs du dossier',
    'dashboard.firm.widget.last-entry': 'Dernière écriture',
    'dashboard.firm.widget.team': 'Équipe',
    'dashboard.firm.widget.dsf-visa': 'Visa DSF',
    'dashboard.stock.widget.quick-actions': 'Actions rapides',
    'dashboard.stock.widget.kpi-articles': 'Articles totaux',
    'dashboard.stock.widget.kpi-low-stock': 'Stock faible (indicateur)',
    'dashboard.stock.widget.kpi-expiring': 'Lots en expiration (indicateur)',
    'dashboard.stock.widget.kpi-value': 'Valeur du stock',
    'dashboard.stock.widget.low-stock-table': 'Stock faible (tableau)',
    'dashboard.stock.widget.expiring-table': 'Lots proches de l’expiration',
    'settings.dashboard.accounting.title': 'Tableau de bord comptable',
    'settings.dashboard.firm.title': 'Tableau de bord cabinet',
    'settings.dashboard.stock.title': 'Tableau de bord des stocks',
    'settings.dashboard.catalogue.description': 'Les blocs affichés et leur ordre pour toute l’entreprise sur cette page. Chaque utilisateur peut ensuite en masquer ou en réordonner pour lui-même, sans jamais afficher un bloc que ses droits ou votre offre n’autorisent pas.',
    'settings.nav.tableauDeBord': 'Tableau de bord',
    'settings.dashboard.title': 'Tableau de bord par défaut',
    'settings.dashboard.description': 'Les blocs affichés et leur ordre pour toute l’entreprise. Chaque utilisateur peut ensuite en masquer ou en réordonner pour lui-même, sans jamais afficher un bloc que son rôle, ses droits ou votre offre n’autorisent pas.',
    'settings.dashboard.save': 'Enregistrer',
    'settings.dashboard.saved': 'Tableau de bord par défaut enregistré.',
    'settings.dashboard.saveFailed': 'Le tableau de bord par défaut n’a pas pu être enregistré.',
    'settings.dashboard.loadFailed': 'Chargement du tableau de bord par défaut impossible.',
    'settings.nav.comptesBancaires': 'Comptes bancaires',
    'settings.nav.parametrageDgi': 'Paramétrage DGI',
    'settings.nav.notifications': 'Notifications',
    'settings.nav.pispi': 'PI-SPI',
    'settings.nav.agentsMcf': 'Agents MCF',
    'settings.nav.appareilsMcf': 'Appareils MCF',
    'settings.nav.pannesMcf': 'Pannes MCF',
    'mcfDevices.loadFailed': 'Chargement des appareils MCF impossible.',
    'mcfDevices.register.title': 'Enregistrer un appareil MCF',
    'mcfDevices.register.description': 'L\'arrêté n°2025-0049 (art. 6 e) demande de détenir un MCF de remplacement. Enregistrez ici le MCF principal et le MCF de remplacement de chaque établissement : une panne ne se déclare que sur un appareil enregistré.',
    'mcfDevices.register.submit': 'Enregistrer l\'appareil',
    'mcfDevices.register.success': 'Appareil {nim} enregistré.',
    'mcfDevices.register.failure': 'L\'appareil n\'a pas pu être enregistré.',
    'mcfDevices.field.establishment': 'Établissement',
    'mcfDevices.field.role': 'Rôle',
    'mcfDevices.field.nim': 'NIM de l\'appareil',
    'mcfDevices.field.nimPlaceholder': 'AB01100008',
    'mcfDevices.field.nimHint': 'Numéro d\'identification du MCF : 2 caractères du fabricant, 2 chiffres du certificat, 6 chiffres de série, sans le compteur d\'activation.',
    'mcfDevices.field.distributor': 'Distributeur',
    'mcfDevices.field.reason': 'Motif du retrait',
    'mcfDevices.role.PRINCIPAL': 'Principal',
    'mcfDevices.role.REMPLACEMENT': 'Remplacement',
    'mcfDevices.reason.DESACTIVE': 'Appareil désactivé',
    'mcfDevices.reason.HORS_USAGE': 'Hors d\'usage',
    'mcfDevices.reason.TRANSFERE': 'Transféré',
    'mcfDevices.status.active': 'Actif',
    'mcfDevices.status.retired': 'Retiré',
    'mcfDevices.distributor': 'Distributeur',
    'mcfDevices.registeredOn': 'Enregistré le',
    'mcfDevices.retiredOn': 'retiré le',
    'mcfDevices.establishmentUnknown': 'Établissement non consultable',
    'mcfDevices.list.title': 'Appareils enregistrés',
    'mcfDevices.list.description': 'Les appareils retirés restent listés. Retirer un appareil termine sa panne éventuellement en cours.',
    'mcfDevices.list.empty': 'Aucun appareil MCF n\'est enregistré : enregistrez l\'appareil de chaque établissement avant de pouvoir déclarer une panne.',
    'mcfDevices.retire.action': 'Retirer',
    'mcfDevices.retire.confirm': 'Retirer l\'appareil {nim} ? Il ne pourra plus faire l\'objet d\'une déclaration de panne, et la panne en cours de cet appareil, s\'il y en a une, sera terminée. Le retrait ne peut pas être annulé.',
    'mcfDevices.retire.confirmAction': 'Confirmer le retrait',
    'mcfDevices.retire.success': 'Appareil {nim} retiré.',
    'mcfDevices.retire.failure': 'L\'appareil n\'a pas pu être retiré.',
    'mcfOutages.loadFailed': 'Chargement des pannes MCF impossible.',
    'mcfOutages.declare.title': 'Déclarer une panne de MCF',
    'mcfOutages.declare.description': 'Un dysfonctionnement du MCF qui bloque la facturation se déclare puis se notifie immédiatement à l\'administration fiscale et au distributeur, ou se signale sur la plateforme de l\'administration fiscale (arrêté n°2025-0049, art. 7 c). Aucun document provisoire ne peut être émis avant l\'enregistrement de cette notification.',
    'mcfOutages.declare.noDevice': 'Aucun appareil MCF n\'est enregistré : enregistrez l\'appareil avant de déclarer une panne.',
    'mcfOutages.declare.devicesLink': 'Paramètres › Appareils MCF',
    'mcfOutages.declare.submit': 'Déclarer la panne',
    'mcfOutages.declare.success': 'Panne déclarée : notifiez-la maintenant.',
    'mcfOutages.declare.failure': 'La panne n\'a pas pu être déclarée.',
    'mcfOutages.field.device': 'Appareil en panne',
    'mcfOutages.field.cause': 'Cause',
    'mcfOutages.field.startedAt': 'Début de la panne',
    'mcfOutages.field.description': 'Description',
    'mcfOutages.field.channel': 'Moyen utilisé',
    'mcfOutages.field.notifiedAt': 'Date et heure de l\'envoi',
    'mcfOutages.field.reference': 'Référence de la notification',
    'mcfOutages.cause.MCF_BLOQUE': 'MCF bloqué',
    'mcfOutages.cause.MCF_DESACTIVE': 'MCF désactivé',
    'mcfOutages.cause.PANNE_MATERIELLE': 'Panne matérielle',
    'mcfOutages.cause.LIAISON_SFE_MCF': 'Liaison coupée entre le logiciel et le MCF',
    'mcfOutages.cause.AGENT_HORS_LIGNE': 'Agent MCF hors ligne',
    'mcfOutages.cause.AUTRE': 'Autre cause',
    'mcfOutages.recipient.ADMINISTRATION_FISCALE': 'Administration fiscale',
    'mcfOutages.recipient.DISTRIBUTEUR': 'Distributeur du MCF',
    'mcfOutages.recipient.PLATEFORME_DGI': 'Plateforme de l\'administration fiscale',
    'mcfOutages.channel.EMAIL': 'E-mail',
    'mcfOutages.channel.TELEPHONE': 'Téléphone',
    'mcfOutages.channel.COURRIER': 'Courrier',
    'mcfOutages.channel.PLATEFORME': 'Plateforme',
    'mcfOutages.channel.EN_PERSONNE': 'En personne',
    'mcfOutages.endCause.DEVICE_CERTIFIED': 'Terminée : le MCF a de nouveau certifié',
    'mcfOutages.endCause.DEVICE_RETIRED': 'Terminée : appareil retiré',
    'mcfOutages.endCause.DECLARED_IN_ERROR': 'Déclarée par erreur',
    'mcfOutages.status.open': 'En cours',
    'mcfOutages.notified': 'Notifiée',
    'mcfOutages.notNotified': 'Non notifiée',
    'mcfOutages.notified.detail': 'La panne est notifiée : l\'article 7 c) de l\'arrêté est satisfait et les documents provisoires peuvent être émis.',
    'mcfOutages.notNotified.detail': 'La panne n\'est pas encore notifiée : notifiez-la à l\'administration fiscale et au distributeur, ou signalez-la sur la plateforme de l\'administration fiscale, puis enregistrez la notification ci-dessous.',
    'mcfOutages.startedOn': 'Début :',
    'mcfOutages.declaredOn': 'déclarée le',
    'mcfOutages.endedOn': 'terminée le',
    'mcfOutages.notify.title': 'Enregistrer la notification',
    'mcfOutages.notify.hint': 'Le logiciel n\'envoie rien : envoyez le texte ci-dessus par vos propres moyens, puis enregistrez ici ce que vous avez fait. Les adresses saisies servent à votre usage et ne sont pas enregistrées.',
    'mcfOutages.notify.textTitle': 'Texte à envoyer',
    'mcfOutages.notify.text': 'Objet : dysfonctionnement du MCF bloquant la facturation (arrêté n°2025-0049, art. 7 c).\nÉtablissement : {establishment}.\nMCF concerné : {nim}.\nCause : {cause}.\nDébut de la panne : {startedAt}.\nPanne déclarée le : {declaredAt}.',
    'mcfOutages.notify.recipients': 'Destinataires de la notification',
    'mcfOutages.notify.address': 'Adresse',
    'mcfOutages.notify.addressPlaceholder': 'Adresse ou numéro utilisé',
    'mcfOutages.notify.recorded': 'Notifications enregistrées',
    'mcfOutages.notify.submit': 'Enregistrer la notification',
    'mcfOutages.notify.success': 'Notification enregistrée.',
    'mcfOutages.notify.failure': 'La notification n\'a pas pu être enregistrée.',
    'mcfOutages.end.action': 'Déclarée par erreur',
    'mcfOutages.end.confirm': 'Terminer cette panne comme déclarée par erreur ? Le serveur le refuse dès qu\'un document provisoire a été émis pendant la panne.',
    'mcfOutages.end.confirmAction': 'Confirmer',
    'mcfOutages.end.success': 'Panne terminée : déclarée par erreur.',
    'mcfOutages.end.failure': 'La panne n\'a pas pu être terminée.',
    'mcfOutages.list.title': 'Pannes déclarées',
    'mcfOutages.list.description': 'Les pannes les plus récentes d\'abord. Une panne se termine d\'elle-même dès que l\'appareil certifie de nouveau une facture.',
    'mcfOutages.list.empty': 'Aucune panne n\'a été déclarée.',
    'postingWaiting.title': 'Règlements en attente de régularisation',
    'postingWaiting.description': "Ces règlements ont été enregistrés sur un document provisoire qui n'a pas de facture de régularisation signée : ils ne sont pas comptabilisés tant qu'elle n'existe pas. Si vous les avez déjà passés vous-même en écriture, marquez-les « Comptabilisé manuellement ».",
    'postingWaiting.action': 'Comptabilisé manuellement',
    'postingWaiting.confirm': "Marquer le règlement du document provisoire {number} « Comptabilisé manuellement » ? Passez d'abord l'écriture d'opérations diverses (OD) de ce règlement : le logiciel ne la crée pas et n'en comptabilisera plus aucune pour ce règlement. Cette action ne peut pas être annulée, et le document provisoire ne pourra plus être régularisé : sa facture de régularisation sera refusée.",
    'postingWaiting.confirmAction': "J'ai passé l'OD : confirmer",
    'postingWaiting.success': 'Règlement du document {number} marqué comptabilisé manuellement.',
    'postingWaiting.failure': "Le règlement n'a pas pu être marqué.",
    'postingWaiting.more': '{count} autre(s) règlement(s) en attente, non listés ici.',
    'settings.nav.miseEnService': 'Mise en service',
    'settings.nav.apparence': 'Apparence des documents',
    'settings.nav.assistantCommandes': 'Assistant › Commandes',
    'stepUp.password': 'Mot de passe',
    'stepUp.code': 'Code de vérification',

    // ─────────────────────────────────────────────────────────────
    // Licence signed by the publisher's former key (#1883)
    // ─────────────────────────────────────────────────────────────
    'licence.formerKey.title': 'Licence à remplacer avant le {day}',
    'licence.formerKey.message':
      "La clé de licence de ce poste est signée par l'ancienne clé de l'éditeur, qui ne sera plus acceptée à partir de cette date : passé le délai de grâce, l'émission de factures sera bloquée. Demandez à l'éditeur une nouvelle clé de licence et saisissez-la dans Paramètres › Mise en service.",
    'licence.formerKey.contactAdmin': 'Contactez votre administrateur.',
    'licence.formerKey.link': 'Installer la nouvelle clé',

    // ─────────────────────────────────────────────────────────────
    // Assistant commands (#505): preview card, confirmation, settings
    // ─────────────────────────────────────────────────────────────
    'commands.preview.quoted': 'Donnée citée, non vérifiée',
    'commands.preview.source.saisi': 'saisi',
    'commands.preview.source.calcule': 'calculé',
    'commands.preview.empty': '(vide)',
    'commands.preview.effects': 'Effets',
    'commands.preview.warnings': 'Points d’attention',
    'commands.preview.confirm': 'Confirmer',
    'commands.preview.cancel': 'Annuler',
    'commands.preview.back': 'Retour',
    'commands.dialog.title': 'Confirmer l’opération',
    'commands.dialog.validUntil': 'Aperçu valable jusqu’à',
    'commands.dialog.stepUp': 'Cette opération demande une confirmation renforcée.',
    'commands.state.done': 'Opération effectuée.',
    'commands.state.open': 'Ouvrir',
    'commands.state.rejected': 'Opération annulée.',
    'commands.state.alreadyDone': 'Opération déjà effectuée.',
    'commands.state.failed': 'L’opération n’a pas été effectuée.',
    'commands.state.executing': 'Opération en cours d’exécution : vérifiez son résultat dans quelques instants.',
    'commands.state.expired': 'L’aperçu a expiré : refaites la demande.',
    'commands.error.noSecret': 'Cette opération ne peut être confirmée que dans la conversation qui l’a proposée : refaites la demande.',
    'commands.error.confirm': 'La confirmation a échoué.',
    'commands.error.reject': 'L’annulation a échoué.',
    'commands.settings.title': 'Commandes de l’assistant',
    'commands.settings.description': 'L’assistant propose une opération ; elle ne s’exécute qu’après votre confirmation. Chaque commande reste désactivée tant que vous ne l’activez pas.',
    'commands.settings.loadError': 'Les réglages des commandes n’ont pas pu être chargés.',
    'commands.settings.commands': 'Commandes',
    'commands.settings.enabled': 'Activée',
    'commands.settings.permission': 'Permission requise',
    'commands.settings.sensitivity.never': 'Confirmation simple',
    'commands.settings.sensitivity.always': 'Confirmation renforcée à chaque fois',
    'commands.settings.sensitivity.ifu-change': 'Confirmation renforcée si l’IFU change',
    'commands.settings.sensitivity.stock-valuation': 'Confirmation renforcée selon la valorisation',
    'commands.settings.limits': 'Limites',
    'commands.settings.limits.hint': 'Laissez un champ vide pour garder la valeur par défaut, affichée en grisé.',
    'commands.settings.limits.proposalsPerUserPerHour': 'Propositions par utilisateur et par heure',
    'commands.settings.limits.executionsPerUserPerHour': 'Exécutions par utilisateur et par heure',
    'commands.settings.limits.executionsPerTenantPerDay': 'Exécutions par entreprise et par jour',
    'commands.settings.limits.stepUpValuation': 'Valorisation à partir de laquelle la confirmation est renforcée (0 = toujours)',
    'commands.settings.limits.maxAmountPerUserPerDay': 'Montant maximal par utilisateur et par jour',
    'commands.settings.limits.currency': 'Devise',
    'commands.settings.limits.amount': 'Montant',
    'commands.settings.limits.addCurrency': 'Ajouter une devise',
    'commands.settings.limits.remove': 'Retirer',
    'commands.settings.limits.invalid': 'Valeur invalide : un nombre entier positif est attendu.',
    'commands.settings.limits.invalidAmount': 'Montant invalide : au plus deux décimales, sans espace.',
    'commands.settings.limits.invalidCurrency': 'Code devise invalide : trois lettres.',
    'commands.settings.save': 'Enregistrer',
    'commands.settings.saved': 'Réglages de la commande enregistrés',
    'commands.settings.saveError': 'Les réglages n’ont pas pu être enregistrés.',
    'commands.settings.switches': 'Coupe-circuits',
    'commands.settings.switches.description': 'Un coupe-circuit actif refuse toute nouvelle proposition et toute confirmation de l’assistant, quelle que soit l’activation de la commande. Les menus de l’application continuent de fonctionner.',
    'commands.settings.switches.all': 'Toutes les commandes',
    'commands.settings.switches.reason': 'Motif',
    'commands.settings.switches.cutOff': 'Couper',
    'commands.settings.switches.restore': 'Rétablir',
    'commands.settings.switches.active': 'Coupé',
    'commands.settings.switches.inactive': 'En service',
    'commands.settings.switches.platform': 'Coupé par l’éditeur',
    'commands.settings.switches.reasonRequired': 'Indiquez le motif.',
    'commands.settings.switches.saved': 'Coupe-circuit enregistré',
    'commands.settings.switches.error': 'Le coupe-circuit n’a pas pu être enregistré.',
    'commands.settings.toCheck': 'Opérations à vérifier',
    'commands.settings.toCheck.description': 'Ces opérations confirmées n’ont pas signalé leur fin depuis plus de deux minutes. Vérifiez dans l’écran concerné si l’écriture a eu lieu, puis indiquez le résultat. Elles ne sont jamais rejouées.',
    'commands.settings.toCheck.none': 'Aucune opération à vérifier.',
    'commands.settings.toCheck.confirmedAt': 'Confirmée le',
    'commands.settings.toCheck.executed': 'Effectuée',
    'commands.settings.toCheck.failed': 'Non effectuée',
    'commands.settings.toCheck.saved': 'Opération marquée',
    'commands.settings.toCheck.error': 'L’opération n’a pas pu être marquée.',

    // ─────────────────────────────────────────────────────────────
    // Settings — Tableaux de paramétrage DGI (SFE §2.35)
    // ─────────────────────────────────────────────────────────────
    'settings.parameterTables.title': 'Tableaux de paramétrage DGI',
    'settings.parameterTables.description':
      "Les quatre tableaux de l'annexe DGI : types du client, types d'article, natures de facture d'avoir et lignes de commentaires. Ajout seul : une ligne s'ajoute, un champ vide se complète, rien ne se modifie ni ne se supprime.",
    'settings.parameterTables.badge.dgi': 'DGI',
    'settings.parameterTables.badge.tenant': 'Ajouté',
    'settings.parameterTables.appendOnly': 'Ajout seul',
    'settings.parameterTables.addRow': 'Ajouter une ligne',
    'settings.parameterTables.complete': 'Compléter',
    'settings.parameterTables.commentLinesFixed':
      "Les lignes de commentaires sont fixées par l'annexe DGI (A à H) : seules les lignes réservées C à H peuvent être complétées.",
    'settings.parameterTables.column.code': 'Code',
    'settings.parameterTables.column.attributes': 'Propriétés',
    'settings.parameterTables.column.source': 'Source',
    'settings.parameterTables.blank': 'Non renseigné',
    'settings.parameterTables.readOnlyHint':
      'Seul un administrateur (permission « Tableaux de paramétrage DGI ») peut ajouter ou compléter des lignes.',
    'settings.parameterTables.cancel': 'Annuler',
    'settings.parameterTables.save': 'Enregistrer',

    // Settings › Obligation de certification — voluntary certification (#528)
    'settings.certification.voluntary.label': 'Certifier volontairement toutes les factures à compter de la date d’effet',
    'settings.certification.voluntary.help':
      'Pour une entreprise non soumise (petite entreprise, micro-entreprise) : après validation par la plateforme, ses factures sont certifiées par le MCF à compter de cette date. Ce choix est définitif.',
    'settings.certification.voluntary.badge': 'Certification volontaire',
    'settings.certification.voluntary.today': 'Soumise par certification volontaire : factures certifiées par le MCF',
    'settings.certification.voluntary.declared':
      'Certification volontaire enregistrée : elle prendra effet après validation par la plateforme',

    // ─────────────────────────────────────────────────────────────
    // Invoice Detail
    // ─────────────────────────────────────────────────────────────
    'invoices.detail.notFound': 'Facture introuvable',
    'invoices.detail.notFoundDesc': "La facture demandée n'existe pas ou a été supprimée.",
    'invoices.detail.backToInvoices': 'Retour aux factures',
    'invoices.detail.sign': 'Signer',
    'invoices.detail.recordPayment': 'Enregistrer paiement',
    'invoices.detail.downloading': 'Téléchargement...',
    'invoices.detail.info': 'Informations de la facture',
    'invoices.detail.creationDate': 'Date de création',
    'invoices.detail.fiscalYear': 'Année fiscale',
    'invoices.detail.clientType': 'Type de client',
    'invoices.detail.priceMode': 'Mode de prix',
    'invoices.detail.client': 'Client',
    'invoices.detail.lines': 'Lignes de facture',
    'invoices.detail.articleCount': 'article(s)',
    'invoices.detail.article': 'Article',
    'invoices.detail.tax': 'Taxe',
    'invoices.detail.qty': 'Qté',
    'invoices.detail.unitPrice': 'P.U.',
    'invoices.detail.discount': 'Remise',
    'invoices.detail.totalHT': 'Total HT',
    'invoices.detail.tva': 'TVA',
    'invoices.detail.totalTTC': 'Total TTC',
    'invoices.detail.taxSummary': 'Récapitulatif TVA',
    'invoices.detail.taxGroup': 'Groupe',
    'invoices.detail.taxRate': 'Taux',
    'invoices.detail.baseHT': 'Base HT',
    'invoices.detail.taxAmount': 'Montant TVA',
    'invoices.detail.paymentHistory': 'Historique des paiements',
    'invoices.detail.notes': 'Notes',
    'invoices.detail.amounts': 'Montants',
    'invoices.detail.paid': 'Payé',
    'invoices.detail.remaining': 'Reste à payer',
    'invoices.detail.mcfSignature': 'Signature MCF',
    'invoices.detail.nim': 'NIM',
    'invoices.detail.counter': 'Compteur',
    // Annex certification block (SECEF BF ST 1.0, 38h), labelled as the annex prints it.
    'invoices.detail.secef.code': 'Code SECeF/DGI',
    'invoices.detail.secef.nim': 'SECeF NIM',
    'invoices.detail.secef.counters': 'SECeF Compteurs',
    'invoices.detail.secef.time': 'SECeF Heure',
    'invoices.detail.mcfPending': 'En attente de confirmation MCF',
    // Signatures in doubt: transcription of an MCF report, annex (BFSECEF01) or legacy QR.
    'mcfSignatures.annex.reservedCounters': 'Compteurs réservés',
    'mcfSignatures.qrData': 'Donnée QR (BFSECEF01;… ou BFSECEF;…)',
    'mcfSignatures.annex.qrHelp':
      "QR de l'annexe : il contient le MID, le code SECeF (24 caractères), l'IFU et la date du MCF. " +
      'Relevez aussi les compteurs imprimés « FC/TC FT ». Ce QR ne porte pas de montant : ' +
      'vérifiez le montant sur le rapport X/Z.',
    'mcfSignatures.legacy.qrHelp':
      'Elle contient déjà le code SECeF, le NIM et le montant : les champs ci-dessous sont ' +
      'recoupés avec elle, et toute divergence est refusée.',
    'mcfSignatures.annex.totalCounter': 'Compteur total (TC)',
    'mcfSignatures.legacy.counter': 'Compteur MCF',
    'mcfSignatures.annex.typeCounter': 'Compteur du type (FC)',
    'mcfSignatures.annex.invoiceType': 'Type certifié (FT)',
    'mcfSignatures.machineId': 'Identifiant machine',
    'mcfSignatures.secefCode': 'Code SECeF',
    'mcfSignatures.certifiedNumber': 'Numéro certifié',
    'mcfSignatures.certifiedNumberHelp':
      "Cette vente n'a jamais enregistré sa facture : le numéro que le MCF a certifié doit être " +
      "relevé sur le rapport, sinon la reprise ne peut pas vérifier qu'elle réutilise le bon.",
    'mcfSignatures.finding': 'Constat (obligatoire)',
    // Series of a number presented by a signature naming no établissement.
    'mcfSignatures.serie.pending': 'Série à choisir',
    'mcfSignatures.serie.saleNoSeriesTag': 'Aucune série ne porte ce numéro',
    'mcfSignatures.serie.title': 'Série du numéro (obligatoire)',
    'mcfSignatures.serie.intro':
      "Cette signature ne nomme pas l'établissement qui a présenté ce numéro, et le système " +
      'ne touche à la numérotation que sur votre choix : indiquez à quelle série il appartient.',
    'mcfSignatures.serie.siteConsequence':
      'Retirer le numéro dans un établissement : la série de cet établissement ne le ' +
      "réattribuera jamais, et s'il a été certifié par le MCF, il ne lui sera jamais présenté " +
      "une seconde fois. Le retrait est refusé si le numéro n'appartient pas à cette série ou " +
      "s'il dépasse son prochain numéro : il y laisserait un trou.",
    'mcfSignatures.serie.noneConsequence':
      "Aucune série : aucune série n'est modifiée. Si le MCF avait en réalité certifié ce " +
      "numéro, l'établissement dont la série l'atteindra le présentera de nouveau : le MCF le " +
      "refusera (E013) tant qu'il est son dernier numéro certifié, sinon il le certifiera une " +
      'seconde fois.',
    'mcfSignatures.serie.site': "Retirer le numéro dans la série de l'établissement",
    'mcfSignatures.serie.none': "Il n'appartient à aucune série : laisser toutes les séries en l'état",
    'mcfSignatures.serie.noCandidate':
      "Aucun établissement de ce dossier n'a de série qui porte ce numéro.",
    // Series of a certified number whose signature names no établissement.
    'mcfSignatures.serie.signedIntro':
      "Cette signature certifiée ne nomme pas l'établissement qui a présenté ce numéro : tant que " +
      "sa série n'est pas choisie, aucun établissement dont la série l'atteint ne peut émettre de " +
      'facture. Indiquez à quelle série il appartient :',
    'mcfSignatures.serie.signedSiteConsequence':
      "Rattacher le numéro à un établissement : la série de cet établissement le compte comme " +
      "émis et ne le réattribuera jamais. Le choix est refusé si le numéro n'appartient pas à " +
      "cette série ou s'il dépasse son prochain numéro : il y laisserait un trou.",
    'mcfSignatures.serie.signedNoneConsequence':
      "Aucune série : aucune série n'est modifiée et le numéro n'y est plus retenu. S'il " +
      "appartenait en réalité à un établissement, la série de celui-ci l'atteindra et le " +
      'présentera de nouveau au MCF, qui le refusera (E013) ou le certifiera une seconde fois.',
    'mcfSignatures.serie.signedSite': "Le numéro appartient à la série de l'établissement",
    'mcfSignatures.serie.saleNeedsSite':
      "Cette vente sera finalisée avec le numéro que le MCF a certifié : choisissez " +
      "l'établissement dont la série le porte.",
    'mcfSignatures.serie.choose': 'Choisir la série',
    'mcfSignatures.serie.chooseTitle': 'Choisir la série du numéro certifié',
    'mcfSignatures.serie.chooseAck':
      "J'ai consulté le rapport X/Z du MCF et j'atteste la série de ce numéro.",
    'mcfSignatures.serie.chosen': 'Série enregistrée — la numérotation reprend',
    'mcfSignatures.serie.chosenSale':
      'Série enregistrée — rejouez la vente à la caisse de cet établissement : elle réutilisera ' +
      'la signature conservée',
    'mcfSignatures.serie.saleNoSeries':
      "Aucun établissement de ce dossier, actif ou non, n'a de série du type de cette vente qui " +
      "porte ce numéro : la vente ne peut pas être finalisée avec lui, et aucun choix de série " +
      "n'est possible ici. S'il relève de la série d'un autre type de pièce, les tirages qui " +
      "l'atteignent dans cette série restent refusés. Si la vente figure sur le rapport X/Z, ne " +
      "la déclarez pas non signée, et ne modifiez pas le préfixe de numérotation d'un " +
      'établissement pour la débloquer : un même rang pourrait être émis deux fois. Vérifiez ' +
      'le numéro sur le rapport X/Z, conservez ce rapport et contactez le support.',
    'mcfSignatures.confirmed.saleReplay':
      'Décision enregistrée — rejouez la vente à la caisse, elle réutilisera cette signature',
    'mcfSignatures.confirmed.saleSeriesPending':
      'Décision enregistrée — choisissez maintenant la série du numéro certifié (« Choisir la ' +
      "série » sur cette page) : la vente ne pourra être rejouée à la caisse qu'ensuite",
    'mcfSignatures.confirmed.invoice':
      'Décision enregistrée — la signature sera ré-appliquée automatiquement',
    'mcfSignatures.confirmed.lateCertification':
      'Décision enregistrée — régularisez maintenant la certification tardive sur cette page',
    // Late certification of a number an invoice issued without the MCF already carries.
    'mcfSignatures.late.tag': "Certification tardive d'un numéro émis hors MCF",
    'mcfSignatures.late.certified': 'Le MCF a certifié le numéro',
    'mcfSignatures.late.carriedBy': 'déjà porté par la facture',
    'mcfSignatures.late.issuedWithoutMcf': 'émise hors MCF',
    'mcfSignatures.late.issuedOn': 'le',
    'mcfSignatures.late.dgiStart': 'Démarche auprès de la DGI : déclarez la certification tardive du numéro',
    'mcfSignatures.late.dgiMiddle': 'par le MCF ; ce numéro est aussi celui de la facture',
    'mcfSignatures.late.dgiEnd': 'émise hors MCF.',
    'mcfSignatures.late.confirmFirst':
      "Déclarez d'abord que le MCF a signé, avec les éléments relevés sur le rapport X/Z : " +
      'la régularisation se fait ensuite sur cette page.',
    'mcfSignatures.late.confirmNext':
      'Ensuite : la pièce a déjà été émise hors MCF sous ce numéro, rien ne sera repris ' +
      'automatiquement. Régularisez la certification tardive sur cette page.',
    'mcfSignatures.late.regularise': 'Régulariser la certification tardive',
    'mcfSignatures.late.dialogTitle': 'Régulariser la certification tardive',
    'mcfSignatures.late.consequence':
      'La signature sera close : elle reste enregistrée comme certifiée par le MCF, la facture ' +
      "émise hors MCF n'est pas modifiée et la numérotation n'est pas touchée. Rien n'est repris " +
      'automatiquement ensuite, et la décision ne peut pas être annulée.',
    'mcfSignatures.late.motifPlaceholder':
      'Rapport Z n°42 du 25/07/2026 : numéro 2026-000123 certifié par le MCF',
    'mcfSignatures.late.ack':
      "J'ai consulté le rapport X/Z du MCF : il porte la certification de ce numéro, et je déclare " +
      'cette certification tardive à la DGI.',
    'mcfSignatures.late.submit': 'Enregistrer la régularisation',
    // A number the MCF certified that no invoice carries, for a document issued without the MCF under another number.
    'mcfSignatures.uncarried.tag': 'Numéro certifié par le MCF que ne porte aucune facture',
    'mcfSignatures.uncarried.certified': 'Le MCF a certifié le numéro',
    'mcfSignatures.uncarried.carriedByNone': ", qu'aucune facture de ce dossier ne porte. La pièce a été émise hors MCF sous le numéro",
    'mcfSignatures.uncarried.dgiStart': 'Démarche auprès de la DGI : déclarez la certification par le MCF du numéro',
    'mcfSignatures.uncarried.dgiMiddle': ", qu'aucune facture ne porte ; la pièce a été émise hors MCF sous le numéro",
    'mcfSignatures.uncarried.confirmNext':
      'Ensuite : la pièce a déjà été émise hors MCF sous un autre numéro, rien ne sera repris ' +
      'automatiquement. Régularisez la certification sur cette page.',
    'mcfSignatures.uncarried.consequence':
      'La signature sera close : elle reste enregistrée comme certifiée par le MCF et la pièce émise hors ' +
      "MCF n'est pas modifiée. Le numéro certifié est retiré de la numérotation : aucune facture ne le " +
      "portera. Rien n'est repris automatiquement ensuite, et la décision ne peut pas être annulée.",
    'mcfSignatures.uncarried.ack':
      "J'ai consulté le rapport X/Z du MCF : il porte la certification de ce numéro, qu'aucune facture ne " +
      'porte, et je déclare cette certification à la DGI.',
    // A number the MCF certified late that no invoice carries, for a document it certified under another number.
    'mcfSignatures.uncarriedCertified.tag':
      'Numéro certifié par le MCF que ne porte aucune facture, pièce certifiée sous un autre numéro',
    'mcfSignatures.uncarriedCertified.certified': 'Le MCF a certifié le numéro',
    'mcfSignatures.uncarriedCertified.carriedByNone':
      ", qu'aucune facture de ce dossier ne porte. La pièce a été certifiée par le MCF sous le numéro",
    'mcfSignatures.uncarriedCertified.dgiStart':
      'Démarche auprès de la DGI : déclarez la certification par le MCF du numéro',
    'mcfSignatures.uncarriedCertified.dgiMiddle':
      ", qu'aucune facture ne porte ; la pièce a été certifiée par le MCF sous le numéro",
    'mcfSignatures.uncarriedCertified.consequence':
      "La signature sera close : la pièce reste certifiée par le MCF sous son numéro et n'est pas modifiée. " +
      'Le numéro certifié en plus est retiré de la numérotation : aucune facture ne le portera. Rien ' +
      "n'est repris automatiquement ensuite, et la décision ne peut pas être annulée.",
    // A number the MCF certified twice, under two counters; an invoice carries the first certification.
    'mcfSignatures.certifiedTwice.tag': 'Numéro certifié deux fois par le MCF',
    'mcfSignatures.keptApart':
      "Certification du MCF tenue à l'écart : elle n'est appliquée à aucune pièce et le document reste non " +
      'signé. Vérifiez le rapport X/Z du MCF (motif ci-contre).',
    'mcfSignatures.certifiedTwice.certified': 'Le MCF a certifié deux fois le numéro',
    'mcfSignatures.certifiedTwice.counters': 'compteurs',
    'mcfSignatures.certifiedTwice.and': 'et',
    'mcfSignatures.certifiedTwice.invoice': ': la facture',
    'mcfSignatures.certifiedTwice.carriesFirst': 'porte la première certification',
    'mcfSignatures.certifiedTwice.issuedOn': 'émise le',
    'mcfSignatures.certifiedTwice.dgiStart': 'Démarche auprès de la DGI : déclarez la double certification du numéro',
    'mcfSignatures.certifiedTwice.dgiEnd': "par le MCF ; la numérotation n'est pas modifiée.",
    'mcfSignatures.certifiedTwice.consequence':
      'La signature sera close : la facture qui porte le numéro garde la première certification et ' +
      "n'est pas modifiée, et la numérotation n'est pas touchée. Rien n'est repris automatiquement " +
      'ensuite, et la décision ne peut pas être annulée.',
    'mcfSignatures.certifiedTwice.ack':
      "J'ai consulté le rapport X/Z du MCF : il porte les deux certifications de ce numéro, et je déclare " +
      'cette double certification à la DGI.',
    'mcfSignatures.codeNotOnInvoice.tag': 'Code de sécurité du registre absent de la facture',
    'mcfSignatures.codeNotOnInvoice.recorded': 'Le registre garde pour le numéro',
    'mcfSignatures.codeNotOnInvoice.counter': ', compteur',
    'mcfSignatures.codeNotOnInvoice.invoice': ', un code de sécurité que la facture',
    'mcfSignatures.codeNotOnInvoice.notCarried': 'ne porte pas.',
    'mcfSignatures.codeNotOnInvoice.check': 'Vérifiez ce compteur sur le rapport X/Z du MCF.',
    'mcfSignatures.codeNotOnInvoice.consequence':
      "La signature sera close : la facture garde ses éléments et n'est pas modifiée, et la numérotation n'est " +
      "pas touchée. Rien n'est repris automatiquement ensuite, et la décision ne peut pas être annulée.",
    'mcfSignatures.codeNotOnInvoice.ack': "J'ai consulté le rapport X/Z du MCF pour ce numéro et ce compteur.",
    'mcfSignatures.late.done':
      'Régularisation enregistrée — déclarez la certification tardive à la DGI',
    'xz.settlementsAfterReport.title': 'Règlements enregistrés après le rapport de leur facture',
    'xz.settlementsAfterReport.note': 'Règlements et annulations de règlement enregistrés sur la période, sur des factures ou documents provisoires placés avant la période. Ils ne sont pas compris dans la ventilation par mode de règlement ci-dessus ; chaque montant est le total net du mode, annulations déduites.',
    'xz.provisional.settlements': 'Règlements sur documents provisoires',
    'xz.provisional.title': 'Documents provisoires émis',
    'xz.provisional.note': 'Documents provisoires émis, hors totaux : leurs montants sont comptés dans le rapport de leur facture de régularisation ; leurs règlements sont compris dans la ventilation par mode de règlement ci-dessus (« Règlements sur documents provisoires »).',
    'xz.provisional.count': 'Nombre de documents provisoires émis',
    'xz.provisional.total': 'Total TTC des documents provisoires émis',
    'regulariser.title': 'À régulariser',
    'regulariser.subtitle': 'Documents provisoires émis pendant une panne de MCF, avec leur échéance de régularisation (arrêté n°2025-0049, art. 6 g).',
    'regulariser.back': 'Retour aux rapports',
    'regulariser.loading': 'Chargement…',
    'regulariser.loadError': 'Chargement impossible.',
    'regulariser.empty': 'Aucun document provisoire n\'a été émis.',
    'regulariser.emptyFiltered': 'Aucun document chargé ne correspond à ce statut.',
    'regulariser.filter.outage': 'Panne',
    'regulariser.filter.outageAll': 'Toutes les pannes',
    'regulariser.filter.status': 'Statut',
    'regulariser.filter.statusAll': 'Tous les statuts',
    'regulariser.filter.statusNote': 'Le filtre de statut s\'applique aux documents chargés.',
    'regulariser.col.number': 'Numéro',
    'regulariser.col.type': 'Type',
    'regulariser.col.client': 'Client',
    'regulariser.col.total': 'Total TTC',
    'regulariser.col.establishment': 'Établissement',
    'regulariser.col.outage': 'Panne',
    'regulariser.col.deadline': 'À régulariser avant le',
    'regulariser.col.status': 'Statut',
    'regulariser.col.regularisation': 'Facture de régularisation',
    'regulariser.outageLabel': 'MCF {nim}, depuis le {date}',
    'regulariser.loadMore': 'Charger plus',
    'regulariser.csv': 'CSV',
    'regulariser.csvError': 'Le fichier CSV est indisponible.',
    'regulariser.card.description': 'Documents provisoires émis pendant une panne de MCF, avec leur échéance de 48 heures, leur statut et leur facture de régularisation, avec export CSV.',
    'outageBanner.open': 'Panne du MCF {nim} déclarée le {date}',
    'outageBanner.notified': 'Notification enregistrée (arrêté n°2025-0049, art. 7 c)).',
    'outageBanner.notNotified': 'Panne non notifiée : aucun document provisoire ne peut être émis avant l\'enregistrement de la notification (arrêté n°2025-0049, art. 7 c)).',
    'outageBanner.record': 'Enregistrer la notification',
    'outageBanner.manage': 'Gérer la panne',
    'outageBanner.overdue.one': '{n} document provisoire en retard de régularisation (arrêté n°2025-0049, art. 6 g)',
    'outageBanner.overdue.many': '{n} documents provisoires en retard de régularisation (arrêté n°2025-0049, art. 6 g)',
    'outageBanner.overdue.more': 'Au moins {n} documents provisoires en retard de régularisation (arrêté n°2025-0049, art. 6 g)',
    'outageBanner.overdue.link': 'Voir les documents à régulariser',
    'connexionBanner.read.prefix': 'Lecture du MCF indisponible : ',
    'connexionBanner.read.AGENT_DECONNECTE': 'l\'agent MCF du site n\'est pas connecté. Relancez l\'agent MCF sur le poste où le MCF est branché, puis vérifiez sa connexion au réseau.',
    'connexionBanner.read.AGENT_NON_VERIFIE': 'l\'état de l\'agent MCF n\'a pas encore pu être vérifié par le serveur. Réessayez dans un instant ; si le message persiste, contactez votre installateur agréé.',
    'connexionBanner.read.LECTURE_MCF_ECHOUEE': 'l\'agent MCF n\'a pas pu lire le MCF. Vérifiez que le MCF est branché et allumé, puis relancez l\'agent MCF.',
    'connexionBanner.read.SIGNATURE_INTERROMPUE': 'une signature interrompue sur l\'agent MCF n\'a pas encore d\'issue établie, et l\'agent ne lit pas le MCF d\'ici là. Si elle figure dans « Signatures MCF en doute », le gérant du site l\'arbitre.',
    'connexionBanner.read.SIGNATURE_INTERROMPUE.reprise': ' La lecture reprend aussi après la prochaine facture certifiée sur ce MCF.',
    'connexionBanner.read.AGENT_PROTOCOLE_LEGACY': 'l\'agent MCF du site fonctionne selon l\'ancien protocole (MCF_PROTOCOL=legacy, le réglage par défaut) alors que le serveur signe selon l\'annexe. Réglez MCF_PROTOCOL=secef (ou « mcfProtocol: secef » dans agent.yml) sur l\'agent, puis relancez-le ; une version de l\'agent qui ne propose pas ce réglage doit d\'abord être mise à jour.',
    'connexionBanner.read.arbitrationLink': 'Ouvrir « Signatures MCF en doute »',
    'invoices.detail.signatureDate': 'Date de signature',
    'invoices.detail.qrCode': 'Code QR',
    'invoices.detail.originalInvoice': "Facture d'origine",
    'invoices.detail.originalInvoiceRrr': "Avoir RRR autonome, sans facture d'origine",
    'invoices.detail.creditNoteNature': "Nature de l'avoir",
    'invoices.detail.sign.title': 'Signer la facture',
    'invoices.detail.sign.desc': 'Cette action va signer la facture via le MCF (module de contrôle de facturation). Une fois signée, la facture ne pourra plus être modifiée.',
    'invoices.detail.sign.warning': 'La signature est irréversible. Assurez-vous que toutes les informations sont correctes avant de continuer.',
    'invoices.detail.sign.signing': 'Signature en cours...',
    'invoices.detail.sign.success': 'Facture signée avec succès',
    'invoices.detail.sign.error': 'Erreur lors de la signature',
    'invoices.detail.issue': 'Émettre',
    'invoices.detail.issue.title': 'Émettre la facture',
    'invoices.detail.issue.desc': "Cette action émet la facture. Une fois émise, elle ne pourra plus être modifiée.",
    'invoices.detail.issue.warning': "L'émission est irréversible. Assurez-vous que toutes les informations sont correctes avant de continuer.",
    'invoices.detail.issue.issuing': 'Émission en cours...',
    'invoices.detail.issue.success': 'Facture émise avec succès',
    'invoices.detail.issue.error': "Erreur lors de l'émission",
    'invoices.detail.payment.title': 'Enregistrer un paiement',
    'invoices.detail.payment.remaining': 'Solde restant:',
    'invoices.detail.payment.amount': 'Montant *',
    'invoices.detail.payment.method': 'Mode de paiement *',
    'invoices.detail.payment.reference': 'Référence (optionnel)',
    'invoices.detail.payment.referencePlaceholder': 'Numéro de chèque, référence virement...',
    'invoices.detail.payment.recording': 'Enregistrement...',
    'invoices.detail.payment.record': 'Enregistrer',
    'invoices.detail.payment.invalidAmount': 'Veuillez saisir un montant valide',
    'invoices.detail.payment.exceedsBalance': 'Le montant ne peut pas dépasser le solde restant',
    'invoices.detail.payment.success': 'Paiement enregistré avec succès',
    'invoices.detail.payment.error': "Erreur lors de l'enregistrement du paiement",
    'invoices.detail.pdf.success': 'PDF téléchargé avec succès',
    'invoices.detail.pdf.error': 'Erreur lors du téléchargement du PDF',
    'provisional.badge': 'DOCUMENT PROVISOIRE',
    'provisional.status.A_REGULARISER': 'À régulariser',
    'provisional.status.EN_RETARD': 'En retard de régularisation',
    'provisional.status.REGULARISEE': 'Régularisée',
    'provisional.status.REGULARISEE_HORS_DELAI': 'Régularisée hors délai',
    'provisional.issue.action': 'Émettre un document provisoire',
    'provisional.issue.panel': 'Aucun MCF ne répond et une panne est déclarée et notifiée (arrêté n°2025-0049, art. 7 c). Cette vente peut être enregistrée comme document provisoire, à régulariser par une facture certifiée dans les 48 heures (art. 6 g).',
    'provisional.issue.refused': 'Document provisoire non proposé :',
    'provisional.issue.success': 'Document provisoire émis.',
    'provisional.issue.failure': 'Échec de l\'émission du document provisoire.',
    'regularisation.action': 'Régulariser',
    'regularisation.confirm.title': 'Régulariser le document provisoire',
    'regularisation.confirm.desc': 'Une facture certifiée reprenant cette vente sera émise par le MCF, avec le prochain numéro de la série fiscale. Elle remplace le document provisoire dans les états.',
    'regularisation.confirm.warning': 'L\'émission est irréversible. Les règlements restent enregistrés sur le document provisoire.',
    'regularisation.issuing': 'Régularisation en cours...',
    'regularisation.success': 'Facture de régularisation émise.',
    'regularisation.failure': 'Échec de la régularisation.',
    'regularisation.open': 'Voir la facture de régularisation',
    'regularisation.provisionalNotice': 'Ce document n\'est pas une facture électronique certifiée. La facture certifiée de régularisation doit être produite au plus tard le {date} (arrêté n°2025-0049/MEF/SG/DGI, art. 6 g).',
    'regularisation.regularisationNotice': 'Facture de régularisation du document provisoire n° {numero} du {date} (arrêté n°2025-0049/MEF/SG/DGI, art. 6 g).',
    'regularisation.openProvisional': 'Voir le document provisoire',
    'regularisation.paymentOnProvisional': 'Enregistrez le règlement sur le document provisoire {numero}',
    'clientDetail.provisional.settledBy': 'Dû porté par la facture de régularisation {number}',
    'provisional.deadline': 'À régulariser au plus tard le',
    'invoices.detail.paymentMethod.ESPECES': 'Espèces',
    'invoices.detail.paymentMethod.VIREMENT': 'Virement bancaire',
    'invoices.detail.paymentMethod.CHEQUE': 'Chèque',
    'invoices.detail.paymentMethod.MOBILE_MONEY': 'Mobile Money',
    'invoices.detail.paymentMethod.CARTE_BANCAIRE': 'Carte bancaire',
    'invoices.detail.paymentMethod.CREDIT': 'Crédit',
    'invoices.detail.paymentMethod.AVOIR': 'Avoir',

    // ─────────────────────────────────────────────────────────────
    // POS
    // ─────────────────────────────────────────────────────────────
    'pos.title': 'Point de vente',
    'pos.searchArticle': 'Rechercher un article...',
    'pos.suspended': 'Suspendues',
    'pos.noSession': 'Veuillez ouvrir une session de caisse pour commencer les ventes',
    'pos.allCategories': 'Tous',
    'pos.noArticles': 'Aucun article trouvé',
    'pos.outOfStock': 'Rupture',
    'pos.cart': 'Panier',
    'pos.suspend': 'Suspendre',
    'pos.clearCart': 'Vider',
    'pos.cartEmpty': 'Panier vide',
    'pos.cartEmptyHint': "Cliquez sur un article pour l'ajouter",
    'pos.subtotalHT': 'Sous-total HT',
    'pos.totalTTC': 'Total TTC',
    'pos.pay': 'Payer',
    'pos.clientCash': 'Client comptant',
    'pos.clientNotFound': 'Aucun client trouvé',
    'pos.clientNoName': 'Client sans nom',
    'pos.success.title': 'Paiement réussi',
    'pos.success.change': 'Monnaie:',
    'pos.success.newSale': 'Nouvelle vente',
    'pos.toast.transactionCancelled': 'Transaction annulée',
    'pos.toast.transactionSuspended': 'Transaction suspendue',
    'pos.toast.transactionResumed': 'Transaction reprise',
    'pos.toast.offlineQueued': 'Opération enregistrée hors ligne',
    'pos.error.createTransaction': 'Erreur lors de la création de la transaction',
    'pos.error.addItem': "Erreur lors de l'ajout de l'article",
    'pos.error.updateQty': 'Erreur lors de la mise à jour de la quantité',
    'pos.error.removeItem': "Erreur lors de la suppression de l'article",
    'pos.error.selectClient': 'Erreur lors de la sélection du client',
    'pos.error.cancel': "Erreur lors de l'annulation",
    'pos.error.suspend': 'Erreur lors de la suspension',
    'pos.error.resume': 'Erreur lors de la reprise',
    'pos.error.addPayment': "Erreur lors de l'ajout du paiement",
    'pos.error.removePayment': 'Erreur lors de la suppression du paiement',
    'pos.error.complete': 'Erreur lors de la finalisation',

    // ─────────────────────────────────────────────────────────────
    // Clients
    // ─────────────────────────────────────────────────────────────
    'clients.title': 'Clients',
    'clients.subtitle': 'Gérez votre base de clients',
    'clients.new': 'Nouveau client',
    'clients.search.placeholder': 'Rechercher par nom, email, téléphone, IFU...',
    'clients.filter.allTypes': 'Tous les types',
    'clients.filter.includeInactive': 'Inclure les inactifs',
    'clients.filter.ifuNonConforme': 'IFU non conforme',
    'clients.ifuBanner.count': 'Clients à IFU non conforme pour une facture locale',
    'clients.ifuBanner.detail':
      "Leur type exige un IFU au format officiel (8 chiffres + 1 lettre majuscule) : ils ne peuvent pas recevoir de facture FV, FT ou FA. Un client étranger facturé uniquement à l'exportation peut conserver son identifiant.",
    'clients.ifuBanner.show': 'Afficher ces clients',
    'clients.table.type': 'Type',
    'clients.table.name': 'Nom',
    'clients.table.ifu': 'IFU',
    'clients.table.phone': 'Téléphone',
    'clients.table.email': 'Email',
    'clients.table.loyalty': 'Fidélité',
    'clients.table.actions': 'Actions',
    'clients.type.CC': 'Comptant',
    'clients.type.PM': 'Personne Morale',
    'clients.type.PP': 'Personne Physique',
    'clients.type.PC': 'Commerçant',
    'clients.type.CC.full': 'Client Comptant',
    'clients.type.PM.full': 'Personne Morale',
    'clients.type.PP.full': 'Personne Physique',
    'clients.type.PC.full': 'Personne Physique Commerçant',
    'clients.loyalty.BRONZE': 'Bronze',
    'clients.loyalty.SILVER': 'Argent',
    'clients.loyalty.GOLD': 'Or',
    'clients.empty.title': 'Aucun client trouvé',
    'clients.empty.description': 'Aucun client ne correspond à vos critères de recherche. Essayez de modifier vos filtres ou créez un nouveau client.',
    'clients.error.loadFailed': 'Une erreur est survenue lors du chargement des clients.',
    'clients.delete.confirm': 'Confirmer la suppression',
    'clients.delete.message': 'Êtes-vous sûr de vouloir supprimer',
    'clients.delete.irreversible': 'Cette action est irréversible.',
    'clients.delete.success': 'Client supprimé avec succès',
    'clients.delete.error': 'Erreur lors de la suppression du client',
    'clients.tooltip.viewDetails': 'Voir les détails',
    'clients.tooltip.edit': 'Modifier',
    'clients.tooltip.delete': 'Supprimer',

    // ─────────────────────────────────────────────────────────────
    // Client Create
    // ─────────────────────────────────────────────────────────────
    'clients.create.title': 'Nouveau client',
    'clients.create.subtitle': 'Ajoutez un nouveau client à votre base de données',
    'clients.create.formTitle': 'Informations du client',
    'clients.create.formDesc': 'Remplissez les informations ci-dessous. Les champs obligatoires dépendent du type de client sélectionné.',
    'clients.create.success': 'Client créé avec succès',
    'clients.create.error': 'Erreur lors de la création du client',

    // ─────────────────────────────────────────────────────────────
    // Client Detail
    // ─────────────────────────────────────────────────────────────
    'clients.detail.notFound': 'Client introuvable',
    'clients.detail.notFoundDesc': "Le client demandé n'existe pas ou a été supprimé.",
    'clients.detail.backToClients': 'Retour aux clients',
    'clients.detail.info': 'Informations du client',
    'clients.detail.companyName': 'Nom / Raison sociale',
    'clients.detail.firstName': 'Prénom',
    'clients.detail.ifu': 'IFU',
    'clients.detail.rccm': 'RCCM',
    'clients.detail.phone': 'Téléphone',
    'clients.detail.email': 'Email',
    'clients.detail.address': 'Adresse',
    'clients.detail.internalCode': 'Code interne',
    'clients.detail.tags': 'Tags',
    'clients.detail.tabInvoices': 'Factures',
    'clients.detail.tabNotes': 'Notes / Activité',
    'clients.detail.noInvoices': 'Aucune facture pour ce client',
    'clients.detail.createInvoice': 'Créer une facture',
    'clients.detail.viewAllInvoices': 'Voir toutes les factures',
    'clients.detail.invoiceError': 'Erreur lors du chargement des factures',
    'clients.detail.notes': 'Notes',
    'clients.detail.noNotes': 'Aucune note',
    'clients.detail.activity': 'Activité',
    'clients.detail.clientCreated': 'Client créé',
    'clients.detail.lastModified': 'Dernière modification',
    'clients.detail.loyaltyProgram': 'Programme de fidélité',
    'clients.detail.loyaltyLevel': 'Niveau',
    'clients.detail.loyaltyPoints': 'Points',
    'clients.detail.totalPurchases': 'Total des achats',
    'clients.detail.totalPurchasesHint': "Calculer via l'historique des factures",
    'clients.detail.statistics': 'Statistiques',
    'clients.detail.statStatus': 'Statut',
    'clients.detail.statType': 'Type',
    'clients.detail.statCreatedAt': 'Créé le',
    'clients.detail.noName': 'Client sans nom',
    'clients.detail.inactive': 'Inactif',
    'clients.detail.deleteWarning': 'Toutes les données associées à ce client seront supprimées. Les factures existantes conserveront les informations du client.',
    'clients.detail.deleteSuccess': 'Client supprimé avec succès',
    'clients.detail.deleteError': 'Erreur lors de la suppression',

    // ─────────────────────────────────────────────────────────────
    // Articles
    // ─────────────────────────────────────────────────────────────
    'articles.title': 'Articles',
    'articles.subtitle': 'Gérez votre catalogue de produits et services',
    'articles.new': 'Nouvel article',
    'articles.export': 'Exporter (CSV)',
    'articles.export.error': "L'export des articles a échoué",
    'articles.search.placeholder': 'Code, nom ou description...',
    'articles.filter.allTypes': 'Tous les types',
    'articles.filter.allCategories': 'Toutes les catégories',
    'articles.filter.favoritesOnly': 'Favoris seulement',
    'articles.filter.includeInactive': 'Inclure inactifs',
    'articles.table.code': 'Code',
    'articles.table.name': 'Désignation',
    'articles.table.type': 'Type',
    'articles.table.category': 'Catégorie',
    'articles.table.unitPrice': 'Prix unitaire',
    'articles.table.stock': 'Stock',
    'articles.table.status': 'Statut',
    'articles.table.favorite': 'Favori',
    'articles.table.actions': 'Actions',
    'articles.type.LOCBIE': 'Bien local',
    'articles.type.LOCSER': 'Service local',
    'articles.type.IMPBIE': 'Bien importé',
    'articles.type.IMPSERV': 'Service importé',
    'articles.type.good': 'Bien',
    'articles.type.service': 'Service',
    'articles.empty.title': 'Aucun article',
    'articles.empty.filtered': 'Aucun article trouvé',
    'articles.empty.description': 'Commencez par créer votre premier article pour gérer votre catalogue de produits et services.',
    'articles.empty.filteredDescription': 'Aucun article ne correspond à vos critères de recherche. Essayez de modifier vos filtres.',
    'articles.error.loadFailed': 'Une erreur est survenue lors du chargement des articles.',
    'articles.delete.confirm': 'Êtes-vous sûr de vouloir supprimer l\'article',
    'articles.delete.success': 'Article supprimé avec succès',
    'articles.delete.error': "Erreur lors de la suppression de l'article",
    'articles.favorite.added': 'Article ajouté aux favoris',
    'articles.favorite.removed': 'Article retiré des favoris',
    'articles.favorite.error': 'Erreur lors de la mise à jour du favori',
    'articles.favorite.addTooltip': 'Ajouter aux favoris',
    'articles.favorite.removeTooltip': 'Retirer des favoris',
    'articles.tooltip.viewDetails': 'Voir les détails',
    'articles.tooltip.edit': 'Modifier',
    'articles.tooltip.delete': 'Supprimer',
    'articles.stockLow': 'Stock faible',

    // ─────────────────────────────────────────────────────────────
    // Article Create
    // ─────────────────────────────────────────────────────────────
    'articles.create.title': 'Nouvel article',
    'articles.create.subtitle': 'Ajoutez un nouveau produit ou service à votre catalogue',
    'articles.create.backToArticles': 'Retour aux articles',
    'articles.create.success': 'Article créé avec succès',
    'articles.create.error': "Erreur lors de la création de l'article",
    'articles.create.codeExists': 'Ce code article existe déjà. Veuillez en choisir un autre.',

    // ─────────────────────────────────────────────────────────────
    // Stock
    // ─────────────────────────────────────────────────────────────
    'stock.receive.title': 'Réception de stock',
    'stock.receive.subtitle': "Enregistrez l'arrivée d'un nouveau lot de marchandises",
    'stock.receive.backToStock': 'Retour à la vue stock',
    'stock.receive.article': 'Article',
    'stock.receive.batchNumber': 'Numéro de lot',
    'stock.receive.batchPlaceholder': 'Ex: LOT-2024-001',
    'stock.receive.quantity': 'Quantité reçue',
    'stock.receive.unitCost': 'Coût unitaire (optionnel)',
    'stock.receive.unitCostHint': "Le coût d'acquisition par unité pour le suivi de la valeur du stock",
    'stock.receive.expirationDate': "Date d'expiration (optionnel)",
    'stock.receive.expirationHint': 'Renseignez cette date pour les articles périssables',
    'stock.receive.summary': 'Résumé',
    'stock.receive.currentStock': 'Stock actuel:',
    'stock.receive.quantityToAdd': 'Quantité à ajouter:',
    'stock.receive.newStock': 'Nouveau stock:',
    'stock.receive.submit': 'Enregistrer la réception',
    'stock.receive.success': 'Stock réceptionné avec succès',
    'stock.receive.error': 'Erreur lors de la réception du stock',
    'stock.receive.searchPlaceholder': 'Rechercher un article par code ou nom...',
    'stock.receive.searchHint': 'Tapez pour rechercher un article avec gestion de stock',
    'stock.receive.noArticles': 'Aucun article trouvé',
    'stock.receive.validation.article': 'Veuillez sélectionner un article',
    'stock.receive.validation.batch': 'Le numéro de lot est requis',
    'stock.receive.validation.qty': 'La quantité doit être supérieure à 0',
    'stock.receive.validation.cost': 'Le coût unitaire doit être un nombre positif',
    'stock.receive.validation.expiration': "La date d'expiration ne peut pas être dans le passé",

    'stock.movements.title': 'Mouvements de stock',
    'stock.movements.subtitle': 'Historique de toutes les opérations de stock',
    'stock.movements.newReceipt': 'Nouvelle réception',
    'stock.movements.filter.article': 'Article',
    'stock.movements.filter.allArticles': 'Tous les articles',
    'stock.movements.filter.type': 'Type de mouvement',
    'stock.movements.filter.allTypes': 'Tous les types',
    'stock.movements.filter.startDate': 'Date début',
    'stock.movements.filter.endDate': 'Date fin',
    'stock.movements.table.date': 'Date',
    'stock.movements.table.article': 'Article',
    'stock.movements.table.type': 'Type',
    'stock.movements.table.quantity': 'Quantité',
    'stock.movements.table.reference': 'Référence',
    'stock.movements.table.user': 'Utilisateur',
    'stock.movements.type.IN': 'Entrée',
    'stock.movements.type.OUT': 'Sortie',
    'stock.movements.type.ADJUST': 'Ajustement',
    'stock.movements.type.TRANSFER': 'Transfert',
    'stock.movements.type.LOSS': 'Perte',
    'stock.movements.type.RETURN': 'Retour',
    'stock.movements.empty.title': 'Aucun mouvement',
    'stock.movements.empty.filtered': 'Aucun mouvement trouvé',
    'stock.movements.empty.description': 'Les mouvements de stock apparaîtront ici une fois que vous aurez effectué des opérations.',
    'stock.movements.empty.filteredDescription': 'Aucun mouvement ne correspond à vos critères de recherche. Essayez de modifier vos filtres.',
    'stock.movements.error.loadFailed': 'Une erreur est survenue lors du chargement des mouvements.',
    'stock.movements.articleUnavailable': 'Article non disponible',

    // ─────────────────────────────────────────────────────────────
    // Reports
    // ─────────────────────────────────────────────────────────────
    'reports.title': 'Rapports',
    'reports.subtitle': 'Rapports DGI et analyses des ventes',
    'reports.todayStats': 'Statistiques du jour',
    'reports.noDataToday': "Aucune donnée disponible pour aujourd'hui",
    'reports.stat.invoices': 'Factures',
    'reports.stat.totalHT': 'Total HT',
    'reports.stat.totalTVA': 'Total TVA',
    'reports.stat.totalTTC': 'Total TTC',
    'reports.xReport.title': 'Rapport X (quotidien)',
    'reports.xReport.desc': 'Consultez le résumé des ventes de la journée sans clôture. Répartition par type de facture, mode de paiement et groupe de taxe.',
    'reports.zReport.title': 'Clôture Z',
    'reports.zReport.desc': 'Clôturez la journée de caisse et générez un rapport Z officiel. Action irréversible.',
    'reports.salesReport.title': 'Rapport des ventes',
    'reports.salesReport.desc': 'Analysez vos ventes sur une période donnée. Identifiez vos meilleurs clients et articles les plus vendus.',
    'reports.fecExport.title': 'Export FEC',
    'reports.fecExport.desc': 'Générez le Fichier des Écritures Comptables pour les contrôles fiscaux.',

    // ─────────────────────────────────────────────────────────────
    // X Report (Daily)
    // ─────────────────────────────────────────────────────────────
    'reports.x.title': 'Rapport X',
    'reports.x.subtitle': 'Résumé quotidien des ventes (sans clôture)',
    'reports.x.dateLabel': 'Date du rapport',
    'reports.x.invoiceCount': 'Nombre de factures',
    'reports.x.byInvoiceType': 'Par type de facture',
    'reports.x.byPaymentMethod': 'Par mode de paiement',
    'reports.x.byTaxGroup': 'Récapitulatif par groupe de taxe',
    'reports.x.noData': 'Aucune donnée',
    'reports.x.noInvoices': "Aucune facture n'a été enregistrée pour le",
    'reports.x.error': 'Une erreur est survenue lors du chargement du rapport.',
    'reports.x.emptyInvoices': 'Aucune facture pour cette date',
    'reports.x.emptyPayments': 'Aucun paiement pour cette date',
    'reports.x.emptyTax': 'Aucune donnée fiscale pour cette date',
    'reports.x.printHeader': 'RAPPORT X',
    'reports.x.printFooter': 'Rapport généré le',
    // The neutral brand: the « Certifié » mention is added only by
    // printBrand (lib/homologation.ts), from the server's homologated answer.
    'reports.x.printBrand': 'Ohalys Facturation — SFE 1.0.0',
    'reports.x.tableType': 'Type',
    'reports.x.tableCount': 'Nombre',
    'reports.x.tableAmount': 'Montant TTC',
    'reports.x.tableMode': 'Mode',
    'reports.x.tableGroup': 'Groupe',
    'reports.x.tableBaseHT': 'Base HT',

    // ─────────────────────────────────────────────────────────────
    // Z Report
    // ─────────────────────────────────────────────────────────────
    'reports.z.title': 'Clôture Z',
    'reports.z.subtitle': 'Clôture quotidienne de caisse (DGI)',
    'reports.z.closeDay': 'Clôturer la journée du',
    'reports.z.closeDayDesc': 'Cette action finalise toutes les opérations de la journée et génère un rapport Z officiel. Une fois clôturée, aucune modification ne sera possible.',
    'reports.z.closeButton': 'Clôturer la journée',
    'reports.z.confirm.title': 'Confirmer la clôture',
    'reports.z.confirm.warning': 'Attention : Cette action est irréversible',
    'reports.z.confirm.point1': 'Toutes les factures du jour seront clôturées',
    'reports.z.confirm.point2': 'Aucune modification ne sera possible',
    'reports.z.confirm.point3': 'Un rapport Z officiel sera généré',
    'reports.z.confirm.summary': 'Résumé de la journée :',
    'reports.z.history': 'Historique des clôtures',
    'reports.z.table.date': 'Date',
    'reports.z.table.closedBy': 'Clôture par',
    'reports.z.table.invoices': 'Factures',
    'reports.z.table.totalTTC': 'Total TTC',
    'reports.z.table.actions': 'Actions',
    'reports.z.table.view': 'Voir',
    'reports.z.empty': 'Aucune clôture',
    'reports.z.emptyDesc': "Aucun rapport Z n'a encore été généré. Clôturez votre première journée pour commencer.",
    'reports.z.closed': 'Clôturé',
    'reports.z.invoiceRange': 'Plage de factures',
    'reports.z.byInvoiceType': 'Par type de facture',
    'reports.z.success': 'Journée clôturée avec succès',
    'reports.z.error': 'Erreur lors de la clôture de la journée',

    // ─────────────────────────────────────────────────────────────
    // Sales Report
    // ─────────────────────────────────────────────────────────────
    'reports.sales.title': 'Rapport des ventes',
    'reports.sales.subtitle': 'Analysez vos performances commerciales',
    'reports.sales.exportCSV': 'Exporter CSV',
    'reports.sales.from': 'Du',
    'reports.sales.to': 'Au',
    'reports.sales.presetToday': "Aujourd'hui",
    'reports.sales.presetWeek': 'Cette semaine',
    'reports.sales.presetMonth': 'Ce mois',
    'reports.sales.presetQuarter': 'Ce trimestre',
    'reports.sales.revenue': "Chiffre d'affaires",
    'reports.sales.invoiceCount': 'Nombre de factures',
    'reports.sales.chart': 'Évolution des ventes',
    'reports.sales.chartEmpty': 'Aucune donnée pour cette période',
    'reports.sales.topClients': 'Top 5 clients',
    'reports.sales.topClientsDesc': 'Clients avec le plus de ventes',
    'reports.sales.topClientsEmpty': 'Aucun client pour cette période',
    'reports.sales.topArticles': 'Top 5 articles',
    'reports.sales.topArticlesDesc': 'Articles les plus vendus',
    'reports.sales.topArticlesEmpty': 'Aucun article pour cette période',
    'reports.sales.noData': 'Aucune donnée',
    'reports.sales.noDataDesc': "Aucune vente n'a été enregistrée pour cette période.",
    'reports.sales.period': 'Période : du',
    'reports.sales.units': 'unités',

    // ─────────────────────────────────────────────────────────────
    // FEC Export
    // ─────────────────────────────────────────────────────────────
    'reports.fec.title': 'Export FEC',
    'reports.fec.subtitle': 'Fichier des Écritures Comptables',
    'reports.fec.generate': 'Générer un export FEC',
    'reports.fec.generateDesc': "Sélectionnez l'exercice fiscal pour lequel vous souhaitez générer le fichier FEC.",
    'reports.fec.fiscalYear': 'Exercice fiscal',
    'reports.fec.dataWarning': 'Attention à la complétude des données',
    'reports.fec.dataWarningDesc': "Assurez-vous que toutes les factures de l'exercice ont été validées et clôturées avant de générer l'export FEC. Seules les factures signées seront incluses dans le fichier.",
    'reports.fec.generateButton': 'Générer le FEC',
    'reports.fec.status.pending': 'En attente',
    'reports.fec.status.pendingDesc': "L'export est en file d'attente...",
    'reports.fec.status.processing': 'En cours',
    'reports.fec.status.processingDesc': 'Génération du fichier FEC en cours...',
    'reports.fec.status.completed': 'Terminé',
    'reports.fec.status.completedDesc': 'Le fichier FEC est prêt au téléchargement.',
    'reports.fec.status.failed': 'Échec',
    'reports.fec.status.failedDesc': 'Une erreur est survenue lors de la génération.',
    'reports.fec.entriesExported': 'écritures exportées',
    'reports.fec.generatedAt': 'Généré le',
    'reports.fec.newExport': 'Nouvel export',
    'reports.fec.exportSuccess': 'Export lancé avec succès',
    'reports.fec.exportError': "Erreur lors du lancement de l'export",
    'reports.fec.fileContent': 'Contenu du fichier',

    // ─────────────────────────────────────────────────────────────
    // Admin - Users
    // ─────────────────────────────────────────────────────────────
    'admin.users.title': 'Utilisateurs',
    'admin.users.subtitle': 'Gérez les utilisateurs et leurs accès',
    'admin.users.new': 'Nouvel utilisateur',
    'admin.users.search.placeholder': 'Rechercher par nom ou email...',
    'admin.users.filter.includeInactive': 'Inclure les inactifs',
    'admin.users.table.name': 'Nom',
    'admin.users.table.email': 'Email',
    'admin.users.table.roles': 'Rôles',
    'admin.users.table.status': 'Statut',
    'admin.users.table.lastLogin': 'Dernière connexion',
    'admin.users.table.actions': 'Actions',
    'admin.users.empty.title': 'Aucun utilisateur trouvé',
    'admin.users.empty.description': 'Aucun utilisateur ne correspond à vos critères de recherche. Essayez de modifier vos filtres ou créez un nouvel utilisateur.',
    'admin.users.error.loadFailed': 'Une erreur est survenue lors du chargement des utilisateurs.',
    'admin.users.delete.confirm': 'Confirmer la suppression',
    'admin.users.delete.message': "Êtes-vous sûr de vouloir supprimer l'utilisateur",
    'admin.users.delete.irreversible': 'Cette action est irréversible.',
    'admin.users.delete.success': 'Utilisateur supprimé avec succès',
    'admin.users.delete.error': "Erreur lors de la suppression de l'utilisateur",
    'admin.users.tooltip.edit': 'Modifier',
    'admin.users.tooltip.delete': 'Supprimer',

    // ─────────────────────────────────────────────────────────────
    // Admin - User Form
    // ─────────────────────────────────────────────────────────────
    'admin.users.form.createTitle': 'Nouvel utilisateur',
    'admin.users.form.editTitle': "Modifier l'utilisateur",
    'admin.users.form.createSubtitle': 'Créez un nouveau compte utilisateur avec les rôles appropriés',
    'admin.users.form.editSubtitle': "Modifiez les informations et les rôles de l'utilisateur",
    'admin.users.form.title': "Informations de l'utilisateur",
    'admin.users.form.createDesc': 'Remplissez les informations ci-dessous pour créer un nouvel utilisateur.',
    'admin.users.form.editDesc': 'Modifiez les informations ci-dessous.',
    'admin.users.form.email': 'Adresse email',
    'admin.users.form.emailPlaceholder': 'utilisateur@exemple.com',
    'admin.users.form.emailReadOnly': "L'adresse email ne peut pas être modifiée.",
    'admin.users.form.firstName': 'Prénom',
    'admin.users.form.lastName': 'Nom de famille',
    'admin.users.form.password': 'Mot de passe',
    'admin.users.form.passwordPlaceholder': 'Mot de passe sécurisé',
    'admin.users.form.passwordHint': 'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.',
    'admin.users.form.roles': 'Rôles',
    'admin.users.form.noRoles': 'Aucun rôle disponible',
    'admin.users.form.accountStatus': 'Statut du compte',
    'admin.users.form.accountActive': 'Compte actif',
    'admin.users.form.accountActiveHint': "Un compte inactif ne peut pas se connecter à l'application.",
    'admin.users.form.createButton': "Créer l'utilisateur",
    'admin.users.form.saveButton': 'Enregistrer les modifications',
    'admin.users.form.createSuccess': 'Utilisateur créé avec succès',
    'admin.users.form.createError': "Erreur lors de la création de l'utilisateur",
    'admin.users.form.editSuccess': 'Utilisateur modifié avec succès',
    'admin.users.form.editError': "Erreur lors de la modification de l'utilisateur",
    'admin.users.form.notFound': 'Utilisateur introuvable',

    // ─────────────────────────────────────────────────────────────
    // Admin - Roles
    // ─────────────────────────────────────────────────────────────
    'admin.roles.title': 'Rôles',
    'admin.roles.subtitle': 'Gérez les rôles et leurs permissions',
    'admin.roles.new': 'Nouveau rôle',
    'admin.roles.systemNote': 'Les rôles marqués comme "Système" sont pré-configurés et ne peuvent pas être supprimés. Vous pouvez toutefois modifier leurs permissions.',
    'admin.roles.table.name': 'Nom',
    'admin.roles.table.displayName': "Nom d'affichage",
    'admin.roles.table.description': 'Description',
    'admin.roles.table.permissions': 'Permissions',
    'admin.roles.table.type': 'Type',
    'admin.roles.table.actions': 'Actions',
    'admin.roles.system': 'Système',
    'admin.roles.custom': 'Personnalisé',
    'admin.roles.empty.title': 'Aucun rôle trouvé',
    'admin.roles.empty.description': "Aucun rôle n'a été configuré. Créez un nouveau rôle pour commencer à gérer les permissions.",
    'admin.roles.error.loadFailed': 'Une erreur est survenue lors du chargement des rôles.',
    'admin.roles.delete.confirm': 'Confirmer la suppression',
    'admin.roles.delete.message': 'Êtes-vous sûr de vouloir supprimer le rôle',
    'admin.roles.delete.warning': 'Les utilisateurs ayant ce rôle perdront les permissions associées.',
    'admin.roles.delete.success': 'Rôle supprimé avec succès',
    'admin.roles.delete.error': 'Erreur lors de la suppression du rôle',
    'admin.roles.delete.systemForbidden': 'Les rôles système ne peuvent pas être supprimés',
    'admin.roles.tooltip.editPermissions': 'Modifier les permissions',
    'admin.roles.tooltip.systemNoDelete': 'Rôle système non supprimable',

    // ─────────────────────────────────────────────────────────────
    // Admin - Role Editor
    // ─────────────────────────────────────────────────────────────
    'admin.roles.editor.createTitle': 'Nouveau rôle',
    'admin.roles.editor.editTitle': 'Modifier le rôle',
    'admin.roles.editor.createSubtitle': 'Créez un nouveau rôle avec les permissions appropriées',
    'admin.roles.editor.editSubtitle': 'Modifiez les informations et les permissions du rôle',
    'admin.roles.editor.configTitle': 'Configuration du rôle',
    'admin.roles.editor.createDesc': 'Définissez les informations et les permissions du nouveau rôle.',
    'admin.roles.editor.editDesc': 'Modifiez les informations ci-dessous.',
    'admin.roles.editor.roleId': 'Identifiant du rôle',
    'admin.roles.editor.roleIdPlaceholder': 'ex: comptable, gestionnaire_stock',
    'admin.roles.editor.roleIdHint': 'Identifiant technique unique (lettres minuscules et underscores uniquement)',
    'admin.roles.editor.roleIdReadOnly': "L'identifiant du rôle ne peut pas être modifié.",
    'admin.roles.editor.displayName': "Nom d'affichage",
    'admin.roles.editor.displayNamePlaceholder': 'ex: Comptable, Gestionnaire de Stock',
    'admin.roles.editor.description': 'Description',
    'admin.roles.editor.descriptionPlaceholder': 'Description du rôle et de ses responsabilités...',
    'admin.roles.editor.permissions': 'Permissions',
    'admin.roles.editor.permissionsSelected': 'permission(s) sélectionnée(s)',
    'admin.roles.editor.selectAll': 'Tout sélectionner',
    'admin.roles.editor.systemRoleNotice': 'Ce rôle est pré-configuré par le système. Vous pouvez modifier ses permissions, mais il ne peut pas être supprimé.',
    'admin.roles.editor.createButton': 'Créer le rôle',
    'admin.roles.editor.saveButton': 'Enregistrer les modifications',
    'admin.roles.editor.createSuccess': 'Rôle créé avec succès',
    'admin.roles.editor.createError': 'Erreur lors de la création du rôle',
    'admin.roles.editor.editSuccess': 'Rôle modifié avec succès',
    'admin.roles.editor.editError': 'Erreur lors de la modification du rôle',
    'admin.roles.editor.notFound': 'Rôle introuvable',

    // ─────────────────────────────────────────────────────────────
    // Admin - Settings
    // ─────────────────────────────────────────────────────────────
    'admin.settings.title': "Paramètres de l'entreprise",
    'admin.settings.subtitle': 'Configurez les informations et les paramètres de votre entreprise',
    'admin.settings.companyInfo': "Informations de l'entreprise",
    'admin.settings.companyInfoDesc': 'Les informations de base de votre entreprise',
    'admin.settings.companyName': 'Raison sociale',
    'admin.settings.companyNamePlaceholder': "Nom de l'entreprise",
    'admin.settings.ifu': 'IFU (Identifiant Fiscal Unique)',
    'admin.settings.rccm': 'RCCM',
    'admin.settings.rccmPlaceholder': 'Numéro RCCM',
    'admin.settings.rccmHint': 'Registre du Commerce et du Crédit Mobilier',
    'admin.settings.address': 'Adresse',
    'admin.settings.addressPlaceholder': 'Adresse complète',
    'admin.settings.phone': 'Téléphone',
    'admin.settings.phonePlaceholder': '+226 XX XX XX XX',
    'admin.settings.email': 'Email',
    'admin.settings.emailPlaceholder': 'contact@entreprise.bf',
    'admin.settings.businessType': "Type d'activité",
    'admin.settings.businessType.COMMERCE': 'Commerce',
    'admin.settings.businessType.SERVICE': 'Services',
    'admin.settings.businessType.INDUSTRIE': 'Industrie',
    'admin.settings.businessType.ARTISANAT': 'Artisanat',
    'admin.settings.businessType.AUTRE': 'Autre',
    'admin.settings.invoiceSettings': 'Paramètres de facturation',
    'admin.settings.invoiceSettingsDesc': 'Configuration des factures et du MCF',
    'admin.settings.defaultPriceMode': 'Mode de prix par défaut',
    'admin.settings.defaultPriceModeHint': 'Mode utilisé par défaut lors de la création de nouvelles factures',
    'admin.settings.fiscalYear': 'Exercice suivi par le cabinet comptable',
    'admin.settings.fiscalYearHint': "Exercice dont le cabinet comptable voit l’état dans son portefeuille de dossiers. La numérotation des factures suit l’année civile de leur date d’émission : elle repart à 1 au 1er janvier, quel que soit ce réglage.",
    'admin.settings.nimPrefix': 'Préfixe NIM (MCF)',
    'admin.settings.nimPrefixPlaceholder': 'Ex. AB01000001',
    'admin.settings.nimPrefixHint': "Numéro d’identification machine du MCF de l’établissement principal : 2 caractères du fabricant, 2 chiffres du certificat, 6 chiffres de série.",
    'admin.settings.loyaltyProgram': 'Programme de fidélité',
    'admin.settings.loyaltyProgramDesc': 'Configurez votre programme de points de fidélité',
    'admin.settings.loyaltyEnabled': 'Activer le programme de fidélité',
    'admin.settings.loyaltyEnabledHint': 'Les clients accumuleront des points sur leurs achats',
    'admin.settings.loyaltyPoints': 'Points par FCFA',
    'admin.settings.loyaltyPointsPrefix': '1 point pour',
    'admin.settings.loyaltyPointsSuffix': 'FCFA dépensés',
    'admin.settings.loyaltyPointsHint': 'Par exemple: 100 signifie que le client gagne 1 point pour chaque 100 FCFA dépensés',
    'admin.settings.cancelChanges': 'Annuler les modifications',
    'admin.settings.saveSettings': 'Enregistrer les paramètres',
    'admin.settings.success': 'Paramètres enregistrés avec succès',
    'admin.settings.error': "Erreur lors de l'enregistrement des paramètres",
    'admin.settings.notFound': 'Paramètres introuvables',
    'admin.settings.priceTTC': 'TTC (Toutes Taxes Comprises)',
    'admin.settings.priceHT': 'HT (Hors Taxes)',

    // ─────────────────────────────────────────────────────────────
    // Admin - Audit Log
    // ─────────────────────────────────────────────────────────────
    'admin.audit.title': "Journal d'audit",
    'admin.audit.subtitle': "Consultez l'historique des actions effectuées dans le système",
    'admin.audit.filter.user': 'Utilisateur',
    'admin.audit.filter.action': 'Action',
    'admin.audit.filter.resource': 'Ressource',
    'admin.audit.filter.startDate': 'Date début',
    'admin.audit.filter.endDate': 'Date fin',
    'admin.audit.filter.last30Days': '30 derniers jours',
    'admin.audit.filter.search': 'Rechercher...',
    'admin.audit.table.dateTime': 'Date/Heure',
    'admin.audit.table.user': 'Utilisateur',
    'admin.audit.table.action': 'Action',
    'admin.audit.table.resource': 'Ressource',
    'admin.audit.table.id': 'ID',
    'admin.audit.table.details': 'Détails',
    'admin.audit.table.ipAddress': 'Adresse IP',
    'admin.audit.empty.title': "Aucun log d'audit",
    'admin.audit.empty.filtered': 'Aucun log trouvé',
    'admin.audit.empty.description': "Les logs d'audit apparaîtront ici lorsque des actions seront effectuées dans le système.",
    'admin.audit.empty.filteredDescription': 'Aucun log ne correspond à vos critères de recherche. Essayez de modifier vos filtres.',
    'admin.audit.error.loadFailed': "Une erreur est survenue lors du chargement des logs d'audit.",
    'admin.audit.viewDetails': 'Voir les détails',
    'admin.audit.hideDetails': 'Masquer',
    'admin.audit.system': 'Système',
    'admin.audit.featureInDev': 'Fonctionnalité en cours de développement',
    'admin.audit.featureInDevDesc': "Le journal d'audit global sera bientôt disponible. En attendant, vous pouvez consulter l'historique des modifications sur chaque facture individuellement.",
    'admin.audit.viewInvoices': 'Voir les factures',

    // ─────────────────────────────────────────────────────────────
    // Profile
    // ─────────────────────────────────────────────────────────────
    'profile.title': 'Mon profil',
    'profile.subtitle': 'Gérez vos informations personnelles et vos préférences',
    'profile.accountInfo': 'Informations du compte',
    'profile.accountInfoDesc': 'Vos informations personnelles et de connexion',
    'profile.fullName': 'Nom complet',
    'profile.email': 'Adresse email',
    'profile.role': 'Rôle',
    'profile.lastLogin': 'Dernière connexion',
    'profile.never': 'Jamais',
    'profile.notDefined': 'Non défini',
    'profile.defaultRole': 'Utilisateur',
    'profile.changePassword': 'Changer le mot de passe',
    'profile.changePasswordDesc': 'Mettez à jour votre mot de passe pour sécuriser votre compte',
    'profile.currentPassword': 'Mot de passe actuel',
    'profile.currentPasswordPlaceholder': 'Entrez votre mot de passe actuel',
    'profile.newPassword': 'Nouveau mot de passe',
    'profile.newPasswordPlaceholder': 'Entrez votre nouveau mot de passe',
    'profile.confirmPassword': 'Confirmer le nouveau mot de passe',
    'profile.confirmPasswordPlaceholder': 'Confirmez votre nouveau mot de passe',
    'profile.passwordRequirements': 'Exigences du mot de passe',
    'profile.passwordReq.length': 'Au moins 8 caractères',
    'profile.passwordReq.uppercase': 'Une lettre majuscule',
    'profile.passwordReq.lowercase': 'Une lettre minuscule',
    'profile.passwordReq.digit': 'Un chiffre',
    'profile.changePasswordButton': 'Modifier le mot de passe',
    'profile.changePasswordSuccess': 'Mot de passe modifié avec succès',
    'profile.changePasswordError': 'Erreur lors de la modification du mot de passe',
    'profile.preferences': 'Préférences',
    'profile.preferencesDesc': 'Personnalisez votre expérience utilisateur',
    'profile.language': 'Langue',
    'profile.language.fr': 'Français',
    'profile.language.en': 'English',
    'profile.theme': 'Thème',
    'profile.theme.light': 'Clair',
    'profile.theme.dark': 'Sombre',
    'profile.theme.system': 'Système',
    'profile.dateFormat': 'Format de date',
    'profile.sidebar': 'Barre latérale',
    'profile.sidebarCollapsed': 'Réduire par défaut',
    'profile.sidebarCollapsedHint': 'La barre latérale sera réduite au démarrage',
    'profile.preferencesSuccess': 'Préférences mises à jour',
    'profile.preferencesError': 'Erreur lors de la mise à jour des préférences',
    // « À propos » (#259): the suite and SFE versions this server runs.
    'about.title': 'À propos',
    'about.subtitle': 'Les versions du logiciel en service sur ce serveur.',
    'about.version': 'version',
    'about.build.title': 'Version du logiciel',
    'about.build.description':
      "La version de la suite, puis la version et l'empreinte du module de facturation (SFE) en service sur ce serveur.",
    'profile.passkeys': 'Passkeys',
    'profile.passkeys.add': 'Ajouter un passkey',
    'profile.passkeys.desc': 'Connectez-vous avec votre empreinte, votre visage ou le code PIN de votre appareil.',
    'profile.passkeys.namePlaceholder': 'Nom du passkey (optionnel)',
    'profile.passkeys.register': 'Enregistrer',
    'profile.passkeys.registering': 'Configuration...',
    'profile.passkeys.empty': 'Aucun passkey enregistré. Ajoutez-en un pour vous connecter plus rapidement.',
    'profile.passkeys.noName': 'Passkey sans nom',
    'profile.passkeys.synced': 'Synchronisé',
    'profile.passkeys.thisDevice': 'Cet appareil',
    'profile.passkeys.lastUsed': 'Dernière utilisation:',
    'profile.passkeys.deleteConfirm': 'Supprimer ce passkey ?',

    // ─────────────────────────────────────────────────────────────
    // Funding — PTBA activity tree
    //
    // The account of an activity is EXPLICIT and never inherited: these
    // labels state where an activity is postable, and never resolve one.
    // A node that has children aggregates and is not postable.
    // ─────────────────────────────────────────────────────────────
    'funding.activity.tree.expand': "Déplier l'activité",
    'funding.activity.tree.collapse': "Replier l'activité",
    'funding.activity.tree.aggregationTitle':
      "L'imputation se fait sur les feuilles ; ce niveau totalise ses sous-activités.",
    'funding.activity.tree.aggregationBadge': "Poste d'agrégation — non imputable",
    'funding.activity.tree.postableOn': 'Imputable sur',
    'funding.activity.tree.postableAccountUnknown':
      'Imputable — compte rattaché (plan comptable non consultable)',
    'funding.activity.tree.leafWithoutAccount': 'Feuille sans compte — imputable nulle part',
    // A stored link whose target is NOT among the active postable accounts,
    // read from a chart we ARE allowed to read: the account was deactivated
    // or stopped being postable. Same practical outcome as "no account".
    'funding.activity.tree.leafBrokenAccount':
      'Compte rattaché inutilisable — imputable nulle part',
    'funding.activity.tree.accountBroken': 'compte désactivé ou non imputable',
    'funding.activity.tree.savedAccount': 'Compte enregistré :',
    'funding.activity.tree.accountUnknown': 'non consultable',
    'funding.activity.tree.fiscalYear': 'Exercice',
    'funding.activity.tree.addChild': 'Ajouter une sous-activité à',
    'funding.activity.tree.edit': "Modifier l'activité",
    'funding.activity.tree.delete': "Supprimer l'activité",
    'funding.activity.tree.footnote':
      "Une activité qui porte des sous-activités agrège : elle n'est plus proposée à l'imputation, l'imputation se fait sur ses feuilles. Les écritures déjà passées sur cette activité y restent et continuent d'être totalisées avec celles de ses sous-activités.",
    'funding.activity.tree.noAccount': 'aucun compte',
    'funding.activity.tree.accountNotReadable': 'compte non consultable',
    'funding.activity.tree.orphanLeaves': 'Feuilles sans compte',
    'funding.activity.tree.orphanLeavesNone': 'Toutes les feuilles portent un compte comptable.',
    'funding.activity.tree.orphanLeavesHint':
      "Ces activités n'ont pas de sous-activité et pas de compte : elles ne peuvent être imputées sur aucun compte.",
    'funding.activity.tree.brokenLeaves': 'Comptes rattachés inutilisables',
    'funding.activity.tree.brokenLeavesNone':
      'Toutes les feuilles rattachées pointent vers un compte actif et imputable.',
    // Wording deliberately NOT prefixed « Plan comptable non consultable »:
    // the activity FORM already opens with that exact sentence, and two
    // occurrences of it on the same screen say the same thing twice.
    'funding.activity.tree.brokenLeavesUnknown':
      "Rattachements non vérifiables ici : la lecture du plan comptable n'est pas accessible.",
    'funding.activity.tree.brokenLeavesHint':
      "Ces feuilles pointent vers un compte désactivé ou devenu non imputable : elles ne peuvent recevoir aucune écriture. Rattachez-les à un compte actif et imputable.",
    'funding.activity.tree.divergentChildren': 'Enfants divergents',
    'funding.activity.tree.divergentChildrenNone':
      "Aucune sous-activité ne s'écarte du compte de son activité parente.",
    'funding.activity.tree.divergentChildrenHint':
      "Le compte de chaque activité est enregistré tel quel : modifier celui d'une activité parente ne modifie pas ses sous-activités. Écarts à trancher à la main.",
    'funding.activity.tree.divergentParent': '— activité parente',
    'funding.activity.aggregationWarning.before':
      "devient un poste d'agrégation en recevant cette première sous-activité : elle ne sera plus proposée à l'imputation. Les écritures déjà passées sur",
    'funding.activity.aggregationWarning.after': 'y restent.',

    // ─────────────────────────────────────────────────────────────
    // Funding — PTBA activity form (explicit account, parent copy)
    // ─────────────────────────────────────────────────────────────
    'funding.activity.form.childTitle': 'Nouvelle sous-activité du PTBA',
    'funding.activity.form.parent': 'Activité parente :',
    'funding.activity.form.account': "Compte comptable de l'activité (optionnel)",
    'funding.activity.form.removeAccount': 'Retirer le compte',
    'funding.activity.form.accountsUnreadable':
      'Plan comptable non consultable : rattacher un compte à une activité demande la permission de lecture comptable.',
    'funding.activity.form.accountHint':
      "Le compte est enregistré tel quel sur CETTE activité, sans reprise automatique depuis l'activité parente. Sans compte, l'activité n'est imputable sur aucun compte ; une activité qui porte des sous-activités agrège et ne s'impute pas — l'imputation se fait sur ses feuilles.",
    'funding.activity.form.copyParent': "Reprendre le compte de l'activité parente",
    'funding.activity.form.copyParentEmptyBefore': "L'activité parente",
    'funding.activity.form.copyParentEmptyAfter':
      "ne porte aucun compte : il n'y a rien à reprendre.",
    'funding.activity.form.copyParentHintBefore': 'Recopie',
    'funding.activity.form.copyParentFallback': "le compte de l'activité parente",
    'funding.activity.form.copyParentHintMiddle':
      "dans le champ ci-dessus. C'est la valeur du champ qui est enregistrée : modifier plus tard le compte de",
    'funding.activity.form.copyParentHintAfter': 'ne changera pas celui de cette activité.',

    // ─────────────────────────────────────────────────────────────
    // Funding — PTBA yearly rollover
    // ─────────────────────────────────────────────────────────────
    'funding.activity.rollover.button': 'Reconduire le PTBA',
    'funding.activity.rollover.buttonTitle':
      "Recopier l'arbre des activités et leurs comptes vers l'exercice suivant.",
    'funding.activity.rollover.buttonDisabledTitle':
      "Aucune activité à reconduire : saisissez d'abord le PTBA d'un exercice.",
    'funding.activity.rollover.title': 'Reconduire le PTBA sur un nouvel exercice',
    'funding.activity.rollover.submit': 'Reconduire',
    'funding.activity.rollover.intro':
      "Une activité est unique par code ET par exercice : le « 1.1.2 » de l'exercice suivant est une ligne neuve, qui n'hérite spontanément ni de son activité parente ni de son compte comptable. La reconduction recopie le code, le libellé, le compte comptable, l'état actif et la forme de l'arbre — les sous-activités sont rattachées aux activités neuves, jamais à celles de l'exercice précédent.",
    'funding.activity.rollover.project': 'Projet',
    'funding.activity.rollover.sourceYear': 'Exercice à reconduire',
    'funding.activity.rollover.targetYear': 'Exercice cible',
    'funding.activity.rollover.sourceOne': 'activité à reconduire',
    'funding.activity.rollover.sourceMany': 'activités à reconduire',
    'funding.activity.rollover.createdOne': 'activité créée',
    'funding.activity.rollover.createdMany': 'activités créées',
    'funding.activity.rollover.skippedOne': 'activité déjà présente',
    'funding.activity.rollover.skippedMany': 'activités déjà présentes',
    'funding.activity.rollover.targetOccupied':
      'sur cet exercice : elle(s) sera(ont) laissée(s) intacte(s).',
    'funding.activity.rollover.targetEmpty': 'Aucune activité sur cet exercice pour le moment.',
    'funding.activity.rollover.copyBudgets':
      "Reporter aussi les budgets de l'exercice reconduit",
    'funding.activity.rollover.copyBudgetsHint':
      "Décoché par défaut, et c'est voulu : une activité est rebudgétisée chaque exercice. Reporter le montant de l'exercice précédent sans le dire inventerait un budget que personne n'a arbitré. La structure se reconduit, le chiffre se saisit.",
    'funding.activity.rollover.idempotent':
      "L'opération peut être relancée sans risque : une activité déjà présente sur l'exercice cible est laissée telle quelle, jamais dupliquée ni écrasée.",
    'funding.activity.rollover.validation':
      "Choisissez un projet, l'exercice à reconduire et un exercice cible (une année).",
    'funding.activity.rollover.targetRequired':
      "L'exercice cible est obligatoire : indiquez une année (entre 2000 et 2100).",
    'funding.activity.rollover.successPrefix': 'Reconduction du PTBA',
    'funding.activity.rollover.error': 'La reconduction du PTBA a échoué',

    // ─────────────────────────────────────────────────────────────
    // Accounting — analytic activity tree picker
    //
    // Account and project are two INDEPENDENT filters over the same
    // tree, never a cascade. An empty tree is the setup speaking: the
    // picker never falls back on "show every activity".
    // ─────────────────────────────────────────────────────────────
    'accounting.analytic.activity.hintNoAccount': "Choisissez d'abord le compte de la ligne.",
    'accounting.analytic.activity.hintNoProject':
      'Choisissez un projet pour voir ses activités.',
    'accounting.analytic.activity.hintEmpty':
      "Aucune activité de ce projet n'est rattachée à ce compte.",
    'accounting.analytic.activity.projectFilterSuffix': '(filtre)',
    'accounting.analytic.activity.projectFilterTitle':
      "— réduit la liste des activités, ne s'impute pas",
    'accounting.analytic.activity.projectFilterFieldTitle':
      "filtre de l'arbre des activités, sans imputation propre",
    'accounting.analytic.activity.noProjectOption': '— Aucun projet',
    'accounting.analytic.activity.noActivityOption': '— Aucune activité',
    'accounting.analytic.activity.noActivity': 'Aucune activité',
    'accounting.analytic.activity.aggregationTitle':
      "Poste d'agrégation — l'imputation se fait sur ses sous-activités",
    'accounting.analytic.activity.optionalSuffix': '(optionnel)',
    // The exercise is written on every node and on the closed button: the
    // PTBA is reconducted each year, so the same code and the same label
    // exist for 2026 AND for 2027.
    'accounting.analytic.activity.fiscalYear': 'Exercice',
    // A selection that rule §3 no longer allows is DROPPED, and the drop is
    // ANNOUNCED. Silent removal and silent corruption are both bad: the
    // corruption is unrepairable once the entry is POSTED (I2), the removal
    // is only unnoticed — so the removal wins, but only if it is said. Each
    // message names the gesture that caused it, so the accountant knows
    // where to look rather than discovering an empty picker.
    'accounting.analytic.activity.droppedAccount':
      "Activité analytique retirée : elle n'est pas rattachée au nouveau compte de la ligne.",
    'accounting.analytic.activity.droppedFiscalYear':
      "Activité analytique retirée : elle n'appartient pas à l'exercice de la nouvelle date.",

    // ─────────────────────────────────────────────────────────────
    // Accounting — analytic requirements (`AnalyticRequirement`) seen
    // BEFORE the POST refusal.
    //
    // The server refuses at POST (`assertRequiredAnalytics`, 400
    // AXE_ANALYTIQUE_REQUIS) any line whose account matches a
    // requirement's prefix and which carries no join on an axis of the
    // required TYPE. Until now the accountant discovered it there — with
    // no way, on some lines, to satisfy it at all. Two distinct states,
    // because the remedy differs:
    //
    //  · UNSATISFIABLE — nothing is selectable on this line (an empty
    //    activity tree, an axis with no active value). The entry CANNOT
    //    be validated: say so, name the account, and name the screen
    //    where the missing referential is keyed in;
    //  · UNFILLED — values do exist, none is chosen yet. A prompt, not
    //    an alarm: the accountant just picks one.
    //
    // Sentences are COMPOSED (account number and axis label interpolated
    // between the fragments) — `t()` takes no parameters, the same idiom
    // as `funding.activity.aggregationWarning.before` / `.after`.
    // ─────────────────────────────────────────────────────────────
    'accounting.analytic.requirement.blockedIntro':
      'Cette ligne ne pourra pas être validée : le compte',
    'accounting.analytic.requirement.blockedAxis': "exige une imputation sur l'axe",
    'accounting.analytic.requirement.blockedNoValue':
      "et aucune valeur n'est disponible sur cette ligne.",
    'accounting.analytic.requirement.unfilledIntro':
      'Imputation analytique exigée : le compte',
    'accounting.analytic.requirement.unfilledAxis': "exige une valeur sur l'axe",
    'accounting.analytic.requirement.unfilledAction':
      "Choisissez-la avant de valider l'écriture.",
    // Remedies — the screen that actually holds the missing referential.
    // A mirror axis (`isSystem`) is fed from « Financements », never from
    // « Axes analytiques » : sending the accountant to the latter for a
    // funding axis would be the wrong screen.
    'accounting.analytic.requirement.remedyActivities':
      "Rattachez une activité du PTBA à ce compte dans « Financements → Activités (PTBA) ».",
    'accounting.analytic.requirement.remedyCategories':
      "Saisissez les valeurs dans « Financements → Catégories de décaissement ».",
    'accounting.analytic.requirement.remedySources':
      "Saisissez les valeurs dans « Financements → Sources ».",
    'accounting.analytic.requirement.remedyComponents':
      "Saisissez les valeurs dans « Financements → Composantes ».",
    'accounting.analytic.requirement.remedyAgreements':
      "Saisissez les valeurs dans « Financements → Accords ».",
    // Shown when the server aggregated the per-employee pay lines (no payroll:read).
    'accounting.payrollDetailAggregated':
      "Les lignes de paie par salarié sont regroupées par écriture : leur détail est réservé à la permission « Consulter la paie ».",
    // Shown when a cost-centre or analytic filter left the pay lines out (no payroll:read).
    'accounting.payGroupsLeftOut':
      "Les lignes de paie par salarié ne sont pas retenues par ce filtre : leur détail est réservé à la permission « Consulter la paie ».",
    // Shown when an open-item read left out the partly lettered pay entries (no payroll:read).
    'accounting.payGroupsPartlyLettered':
      "Les écritures de paie lettrées en partie seulement ne sont comptées ni dans ces lignes ni dans leurs totaux : leur détail est réservé à la permission « Consulter la paie ».",
    'accounting.analytic.requirement.remedyCostCenters':
      "Créez un centre dans « Centres de coûts ».",
    'accounting.analytic.requirement.remedyAxes':
      "Créez l'axe et ses valeurs dans « Axes analytiques ».",
    // Summary shown next to « Valider » — the per-line hint alone is easy
    // to miss on a long entry. Wording deliberately DISTINCT from the
    // per-line sentences: the same text twice reads as a duplicate.
    'accounting.analytic.requirement.summaryBlockedTitle':
      "Validation impossible en l'état — un axe analytique exigé ne peut pas être renseigné :",
    'accounting.analytic.requirement.summaryUnfilledTitle':
      'Axes analytiques exigés, non renseignés :',
    'accounting.analytic.requirement.summaryLine': 'Ligne',
    'accounting.analytic.requirement.summaryBlockedDetail': 'aucune valeur disponible',
    'accounting.analytic.requirement.summaryUnfilledDetail': 'à renseigner',
    'table.columns.menu': 'Colonnes',
    'table.columns.menuLabel': 'Colonnes affichées',
    'table.columns.moveUp': 'Monter',
    'table.columns.moveDown': 'Descendre',
    'table.columns.required': '(obligatoire)',
    'table.columns.reset': 'Réinitialiser les colonnes',
    'table.columns.saveCompany': "Définir comme défaut de l'entreprise",
    'table.columns.clearCompany': "Retirer le défaut de l'entreprise",
    'table.columns.saveError': "Les colonnes n'ont pas pu être enregistrées",
    'table.clients.type': 'Type',
    'table.clients.name': 'Nom',
    'table.clients.ifu': 'IFU',
    'table.clients.phone': 'Téléphone',
    'table.clients.email': 'Email',
    'table.clients.loyalty': 'Fidélité',
    'table.clients.actions': 'Actions',
    'table.articles.code': 'Code',
    'table.articles.name': 'Désignation',
    'table.articles.type': 'Type',
    'table.articles.category': 'Catégorie',
    'table.articles.price': 'Prix unitaire',
    'table.articles.stock': 'Stock',
    'table.articles.status': 'Statut',
    'table.articles.favorite': 'Favori',
    'table.articles.actions': 'Actions',
    'table.journalEntries.number': 'Numéro',
    'table.journalEntries.date': 'Date',
    'table.journalEntries.journal': 'Journal',
    'table.journalEntries.label': 'Libellé',
    'table.journalEntries.source': 'Origine',
    'table.journalEntries.amount': 'Montant',
    'table.journalEntries.status': 'Statut',
    'table.journalEntries.actions': 'Actions',
    'table.suppliers.name': 'Nom',
    'table.suppliers.type': 'Type',
    'table.suppliers.ifu': 'IFU',
    'table.suppliers.phone': 'Téléphone',
    'table.suppliers.email': 'E-mail',
    'table.suppliers.status': 'Statut',
    'table.suppliers.actions': 'Actions',
    'table.employees.matricule': 'Matricule',
    'table.employees.name': 'Nom',
    'table.employees.position': 'Poste',
    'table.employees.department': 'Département',
    'table.employees.contract': 'Contrat',
    'table.employees.baseSalary': 'Salaire de base',
    'table.employees.cnss': 'N° CNSS',
    'table.employees.status': 'Statut',
    'table.employees.actions': 'Actions',
    'table.quotes.number': 'Numéro',
    'table.quotes.client': 'Client',
    'table.quotes.date': 'Date',
    'table.quotes.validUntil': 'Validité',
    'table.quotes.totalTTC': 'Total TTC',
    'table.quotes.status': 'Statut',
    'table.deliveryNotes.number': 'Numéro',
    'table.deliveryNotes.client': 'Client',
    'table.deliveryNotes.createdAt': 'Créé le',
    'table.deliveryNotes.delivered': 'Livré le / reçu par',
    'table.deliveryNotes.status': 'Statut',
    'table.deliveryNotes.actions': 'Actions',
    'table.stockMovements.date': 'Date',
    'table.stockMovements.article': 'Article',
    'table.stockMovements.type': 'Type',
    'table.stockMovements.location': 'Emplacement',
    'table.stockMovements.quantity': 'Quantité',
    'table.stockMovements.reference': 'Référence',
    'table.stockMovements.user': 'Utilisateur',
    'table.payrollRuns.period': 'Période',
    'table.payrollRuns.status': 'Statut',
    'table.payrollRuns.totalGross': 'Total brut',
    'table.payrollRuns.totalNet': 'Total net',
    'table.payrollRuns.employerCharges': 'Charges patronales',
    'table.payrollRuns.entry': 'Écriture',
    'table.payrollRuns.actions': 'Actions',
    'table.purchaseOrders.number': 'Numéro',
    'table.purchaseOrders.supplier': 'Fournisseur',
    'table.purchaseOrders.date': 'Date',
    'table.purchaseOrders.expectedDate': 'Livraison prévue',
    'table.purchaseOrders.totalHT': 'Total HT',
    'table.purchaseOrders.reception': 'Réception',
    'table.purchaseOrders.status': 'Statut',
    'table.supplierInvoices.number': 'Numéro',
    'table.supplierInvoices.supplierRef': 'Réf. fournisseur',
    'table.supplierInvoices.supplier': 'Fournisseur',
    'table.supplierInvoices.date': 'Date',
    'table.supplierInvoices.dueDate': 'Échéance',
    'table.supplierInvoices.totalTTC': 'Total TTC',
    'table.supplierInvoices.amountPaid': 'Réglé',
    'table.supplierInvoices.status': 'Statut',
    'table.supplierInvoices.paymentStatus': 'Paiement',
    'table.projects.code': 'Code',
    'table.projects.name': 'Projet',
    'table.projects.manager': 'Responsable',
    'table.projects.status': 'Statut',
    'table.projects.progress': 'Avancement',
    'table.projects.budget': 'Budget / Réel',
    'table.projects.tasks': 'Tâches',
    'table.projects.actions': 'Actions',
    'table.crmLeads.name': 'Nom',
    'table.crmLeads.contact': 'Contact',
    'table.crmLeads.source': 'Source',
    'table.crmLeads.assignee': 'Responsable',
    'table.crmLeads.status': 'Statut',
    'table.crmLeads.createdAt': 'Créé le',
    'table.crmLeads.actions': 'Actions',
  },
} as const;

// Downloadable admin guide, named after the current brand. The file itself
// (packages/web/public/…) must be renamed too when the brand changes —
// this constant only stops the path from being duplicated across components.
/**
 * Core's catalogue plus each module's strings. French throughout, per DGI
 * homologation — a module ships its own file and it is folded here so there
 * is still one catalogue to audit.
 *
 * The type stays exactly the base literal's: `TranslationKey` derives from
 * it, and widening it with an index signature makes the key type
 * `string | number | symbol`, which `t()` cannot index by. A module that
 * ships strings of its own therefore adds their keys to the base catalogue;
 * what it folds here are the values.
 */
const moduleMessages: Record<string, string> = REGISTERED_MODULE_UIS.reduce(
  (acc, ui) => Object.assign(acc, ui.messages),
  {} as Record<string, string>
);

export const translations: typeof baseTranslations = {
  ...baseTranslations,
  fr: { ...baseTranslations.fr, ...moduleMessages } as typeof baseTranslations.fr,
};

export const USER_GUIDE_PATH = '/Ohalys-Guide-Utilisateur.docx';

/**
 * Earlier names of the user guide. The static host answers them with the
 * app shell, and the router sends each one on to USER_GUIDE_PATH, so a
 * bookmark or an external link to an old name still downloads the guide.
 */
export const LEGACY_USER_GUIDE_PATHS: readonly string[] = ['/E-Facture-Guide-Utilisateur-Admin.docx'];

export type Locale = keyof typeof translations;

let currentLocale: Locale = 'fr';

export function setLocale(locale: Locale): void {
  currentLocale = locale;
}

export function getLocale(): Locale {
  return currentLocale;
}

let terminologyOverlay: Readonly<Record<string, string>> = {};

/** Sector-specific vocabulary layered over the base catalogue. */
export function setTerminologyOverlay(overlay: Readonly<Record<string, string>>): void {
  terminologyOverlay = overlay;
}

export function clearTerminologyOverlay(): void {
  terminologyOverlay = {};
}

export function t(key: TranslationKey): string {
  return terminologyOverlay[key] ?? translations[currentLocale][key] ?? key;
}

export function useTranslation() {
  return { t, locale: currentLocale, setLocale };
}
